import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as jwtDecodeModule from 'jwt-decode';
import { Card, CardTitle, Collapse } from 'reactstrap';
import { useLocation, useHistory } from 'react-router-dom';
import NotificationAlert from 'react-notification-alert';
import MerchantSelections from './MerchantSelections';
import DirectTypeSelections from './DirectTypeSelections';
import RegularCreation from './RegularCreation';
import PreferredDriverSelect from './DirectComponents/PreferredDriverSelect';
import RegularConfirmPage from './RegularConfirmPage';
import RoutedCreation from './RoutedCreation';
import RoutedConfirmPage from './RoutedConfirmPage';
import ItemizedMultiDropCreation from './ItemizedMultiDropCreation';
import ItemizedMultiDropConfirmPage from './ItemizedMultiDropConfirmPage';
import GophrBillingForm from './GophrBillingForm';
import DropshipForm from '../../DropshipForm/DropshipForm';
import PaymentMethodDisplay from '../../SharedComponents/PaymentMethodDisplay';
import { getStoredStoreInvoiceFrequency } from '../../../services/paymentService';
import {
  getCompanyPaymentTermLabel,
  getCompanyPaymentTermPolicy,
  normalizeCompanyPaymentTerm,
} from '../../../services/paymentTerms';
import { normalizeShipmentVehicleTypeForApi, requiresRangeQuoteForVehicleType } from './vehicleTypeApi';

import * as shipment_urls from '../../../urls/api-shipments/merchant';
import * as inventory_urls from '../../../urls/api-inventory/admin';
import { buildAuthorizationHeader, notifySessionExpired, renewSessionSilently } from '../../../Auth/session';
import { isLocalLikeHost } from '../../../utils/environmentMode';

const LOCAL_CREATE_RETRY_COOLDOWN_MS = 60000;

const isUnauthorizedError = error => {
  const status = error && error.response ? error.response.status : null;
  return status === 401 || status === 403;
};

const runWithSessionRecovery = async requestFactory => {
  try {
    return await requestFactory();
  } catch (error) {
    if (!isUnauthorizedError(error)) {
      throw error;
    }

    try {
      await renewSessionSilently();
      return await requestFactory();
    } catch (_) {
      notifySessionExpired({ reason: 'merchant_create_order_unauthorized' });
      throw error;
    }
  }
};

const getDraftOrderByAction = (shipment, actionName, fallbackIndex) => {
  const shipmentOrders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders : [];
  const matchingOrder = shipmentOrders.find(order => order && order.actions && Array.isArray(order.actions[actionName]));

  return matchingOrder || shipmentOrders[fallbackIndex] || null;
};

const parseDraftItems = (order, shipmentDescription) => {
  const actionItems =
    (order && order.actions && Array.isArray(order.actions.pick_up) && order.actions.pick_up) ||
    (order && order.actions && Array.isArray(order.actions.drop_off) && order.actions.drop_off) ||
    null;

  if (Array.isArray(actionItems) && actionItems.length > 0) {
    return actionItems;
  }

  const description = (order && order.description) || shipmentDescription;

  if (typeof description === 'string' && description.trim() !== '') {
    try {
      const parsedDescription = JSON.parse(description);
      if (Array.isArray(parsedDescription) && parsedDescription.length > 0) {
        return parsedDescription;
      }
    } catch (_) {}
  }

  return [{ quantity: 1, detail: '' }];
};

// Parses a persisted order's items back into the form's [{quantity, detail}] shape. Active orders
// store action items as "qty-detail" strings (backend-normalized), while the order description
// usually holds the original JSON array — prefer that, then fall back to parsing the strings.
const parseOrderItemsForForm = (order, shipmentDescription) => {
  const tryParseObjects = str => {
    if (typeof str !== 'string' || str.trim() === '') {
      return null;
    }
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
        return parsed.map(item => ({ quantity: Number(item.quantity) || 1, detail: item.detail || '' }));
      }
    } catch (_) {}
    return null;
  };

  const fromDescription = order ? tryParseObjects(order.description) : null;
  if (fromDescription) {
    return fromDescription;
  }

  const actionArray =
    order && order.actions && Array.isArray(order.actions.pick_up)
      ? order.actions.pick_up
      : order && order.actions && Array.isArray(order.actions.drop_off)
      ? order.actions.drop_off
      : null;

  if (Array.isArray(actionArray) && actionArray.length > 0) {
    return actionArray.map(item => {
      if (item && typeof item === 'object') {
        return { quantity: Number(item.quantity) || 1, detail: item.detail || '' };
      }
      const text = String(item);
      const dashIndex = text.indexOf('-');
      if (dashIndex > 0) {
        const quantity = Number(text.slice(0, dashIndex));
        return { quantity: Number.isFinite(quantity) ? quantity : 1, detail: text.slice(dashIndex + 1) };
      }
      return { quantity: 1, detail: text };
    });
  }

  const fromShared = tryParseObjects(shipmentDescription);
  return fromShared || [{ quantity: 1, detail: '' }];
};

const orderHasAction = (order, actionKey) =>
  Boolean(order && order.actions && Object.prototype.hasOwnProperty.call(order.actions, actionKey));

const buildDraftRouteSnapshot = shipment => {
  const calculatedRoute = shipment && shipment.shipping_fee_blob && shipment.shipping_fee_blob.calculated;
  const billedRoute = calculatedRoute && (calculatedRoute.billed_route || calculatedRoute.route);

  if (billedRoute && typeof billedRoute === 'object') {
    return billedRoute;
  }

  const distanceMiles = Number(shipment && shipment.distance);
  const quotedHours = Number(shipment && shipment.quoted_time);

  return {
    distance: Number.isFinite(distanceMiles) ? distanceMiles * 1609.344 : 0,
    duration: Number.isFinite(quotedHours) ? quotedHours * 60 * 60 : 0,
  };
};

const resolveDraftStoreId = draftShipment =>
  (draftShipment && draftShipment.customer_blob && draftShipment.customer_blob.store_id) ||
  (draftShipment && draftShipment.shipping_fee_blob && draftShipment.shipping_fee_blob.shipment && draftShipment.shipping_fee_blob.shipment.store_id) ||
  (Array.isArray(draftShipment && draftShipment.Orders) &&
    draftShipment.Orders.find(order => order && order.store_id) &&
    draftShipment.Orders.find(order => order && order.store_id).store_id) ||
  '';

const STORE_STRIPE_BILLING_SUPER_MERCHANT_ROLE = '200';

const getPreferredStoreBillingOwner = storeOwnerships => {
  if (!Array.isArray(storeOwnerships) || storeOwnerships.length === 0) {
    return null;
  }

  const normalizedOwnerships = storeOwnerships.filter(Boolean);
  const superMerchantOwner = normalizedOwnerships.find(owner => String(owner.role || '').trim() === STORE_STRIPE_BILLING_SUPER_MERCHANT_ROLE);

  return superMerchantOwner || normalizedOwnerships[0] || null;
};

const toSafeString = value => (value == null ? '' : String(value));

const resolveJwtDecode = () => {
  if (typeof jwtDecodeModule === 'function') {
    return jwtDecodeModule;
  }

  if (jwtDecodeModule && typeof jwtDecodeModule.jwtDecode === 'function') {
    return jwtDecodeModule.jwtDecode;
  }

  if (jwtDecodeModule && typeof jwtDecodeModule.default === 'function') {
    return jwtDecodeModule.default;
  }

  return null;
};

const CreateDeliveryForMerchant = () => {
  const jwtDecode = resolveJwtDecode();
  const decodedAccess = (() => {
    try {
      const accessToken = window.localStorage.getItem('access_token');
      const normalizedToken = typeof accessToken === 'string' ? accessToken.replace(/^Bearer\s+/i, '').trim() : '';

      if (!normalizedToken || typeof jwtDecode !== 'function' || normalizedToken.split('.').length !== 3) {
        return {};
      }

      return jwtDecode(normalizedToken) || {};
    } catch (_) {
      return {};
    }
  })();
  const SUB = decodedAccess.sub;
  const location = useLocation();
  const history = useHistory();
  const notificationAlertRef = useRef(null);
  const localShipmentCreateRetryAtRef = useRef(0);
  const draftHydratedShipmentIdRef = useRef(null);
  const abandonedPrefillAppliedRef = useRef(null);
  const formTopRef = useRef(null);

  const [selectedType, setSelectedType] = useState('multidrop');
  const [selectedStore, setSelectedStore] = useState('');
  const [showFormSelection, setShowFormSelection] = useState(true);
  const [showForm1, setShowForm1] = useState(false);
  const [showForm2, setShowForm2] = useState(false);
  const [showForm3, setShowForm3] = useState(false);
  const [pickupInfo, setPickupInfo] = useState({});
  const [pickupInstruction, setPickupInstruction] = useState('');
  const [dropoffInfo, setDropoffInfo] = useState({});
  const [dropoffInstruction, setDropoffInstruction] = useState('');

  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(null);
  const [isExpedited, setIsExpedited] = useState(false);
  const [preferredDriver, setPreferredDriver] = useState(null);
  const [isSuperExpedited, setIsSuperExpedited] = useState(false);
  const [isShipTomorrow, setIsShipTomorrow] = useState(false);
  const [, setSellsAlcohol] = useState(false);
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [vehicleType, setVehicleType] = useState('');
  const [numPeople, setNumPeople] = useState(null);
  const [weight, setWeight] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [, setOrderStatus] = useState('awaiting_driver');
  const [, setShipmentStatus] = useState('shipment_ready_for_driver');
  const [price, setPrice] = useState(null);
  const [distance, setDistance] = useState('');
  const [route, setRoute] = useState({});
  const [abandonedPrefillBanner, setAbandonedPrefillBanner] = useState(null);
  const [storeMarketId, setStoreMarketId] = useState('b641bc13-8f56-4f4b-b1ea-c1f833b982bd');

  const [vendorLat, setVendorLat] = useState('');
  const [vendorLong, setVendorLong] = useState('');
  const [storeAddress1, setStoreAddress1] = useState('');
  const [storeAddress2, setStoreAddress2] = useState('');
  const [storeCity, setStoreCity] = useState('');
  const [storeCountry, setStoreCountry] = useState('');
  const [storeId, setStoreId] = useState('');
  const [storeFirstName, setStoreFirstName] = useState(null);
  const [storeLastName, setStoreLastName] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [storeLat, setStoreLat] = useState(null);
  const [storeLng, setStoreLng] = useState(null);
  const [storePhone, setStorePhone] = useState(null);
  const [storeState, setStoreState] = useState('');
  const [storeZip, setStoreZip] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [merchantOrderID, setMerchantOrderID] = useState(null);
  const [merchantAuth0ID, setMerchantAuth0ID] = useState('');
  const [editingSavedShipmentId, setEditingSavedShipmentId] = useState(null);
  const [editingActiveShipmentId, setEditingActiveShipmentId] = useState(null);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [editOrderIds, setEditOrderIds] = useState({});
  const [activeEditShipment, setActiveEditShipment] = useState(null);

  const [defaultItems, setDefaultItems] = useState([{ quantity: 1, detail: '' }]);

  const [errorSubmit, setErrorSubmit] = useState({});
  const [errorMessageDistance, setErrorMessageDistance] = useState({ errorMessage: '', errorCode: 0 });
  const [errorMessageSubmit, setErrorMessageSubmit] = useState({ errorMessage: '', errorCode: 0 });

  //Routed
  const [pickupItems, setPickupItems] = useState([{ quantity: 1, detail: '' }]);
  const [dropoffItems, setDropoffItems] = useState([{ quantity: 1, detail: '' }]);
  const [waypointPickupItems, setWaypointPickupItems] = useState([{ quantity: 1, detail: '' }]);
  const [waypointDropoffItems, setWaypointDropoffItems] = useState([{ quantity: 1, detail: '' }]);
  const [waypointInfo, setWaypointInfo] = useState({});
  const [waypointPickupInstruction, setWaypointPickupInstruction] = useState('');
  const [waypointDropoffInstruction, setWaypointDropoffInstruction] = useState('');
  const [isWaypointPickup, setIsWaypointPickup] = useState(false);
  const [isWaypointDropoff, setIsWaypointDropoff] = useState(false);

  //MultiDrop
  const [multiDropWaypointsArray, setMultiDropWaypointsArray] = useState([
    {
      waypointFirstname: '',
      waypointLastname: '',
      waypointPhone: '',
      waypointAddress1: null,
      waypointAddress2: null,
      waypointFullAddress: '',
      waypointLat: '',
      waypointLng: '',
      waypointCity: '',
      waypointState: '',
      waypointCountry: '',
      waypointZip: '',
      waypointDropoffInstruction: '',
      waypointBusinessName: '',
      waypointCustomerSuggestions: [],
      isWaypointAutoFilled: false,
      dropItems: [{ quantity: 1, detail: '' }],
      dropItemsError: [false]
    }
  ]);
  const [multiDropWaypointsErrors, setMultiDropWaypointsErrors] = useState([
    {
      waypointFirstname: false,
      waypointLastname: false,
      waypointPhone: false,
      waypointAddress: false
    }
  ]);
  const [multiDropsInfo, setMultiDropsInfo] = useState([]);

  //! Itemized infinite drops price
  const [perItemPrice, setPerItemPrice] = useState(null);
  const [perStopPrice, setPerStopPrice] = useState(null);
  const [extraPerItemPrice, setExtraPerItemPrice] = useState(null);
  const [freeItemCount, setFreeItemCount] = useState(null);

  //! Equipments and Tools
  const [isForkLift, setIsForkLift] = useState(false);
  const [isDolly, setIsDolly] = useState(false);
  const [isHandLoadUnload, setIsHandLoadUnload] = useState(false);
  const [isHazmat, setIsHazmat] = useState(false);
  const [isHotBag, setIsHotBag] = useState(false);
  const [isCooler, setIsCooler] = useState(false);
  const [isTWICCard, setIsTWICCard] = useState(false);
  const [isLiftgate, setIsLiftgate] = useState(false);
  const [isPalletJack, setIsPalletJack] = useState(false);
  const [isWeatherProtection, setIsWeatherProtection] = useState(false);
  const [isMedical, setIsMedical] = useState(false);

  const [priceRangeLow, setPriceRangeLow] = useState(null);
  const [priceRangeHigh, setPriceRangeHigh] = useState(null);
  const [standardPriceRangeLow, setStandardPriceRangeLow] = useState('');
  const [standardPriceRangeHigh, setStandardPriceRangeHigh] = useState('');
const toFiniteCurrency = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};
  const [expeditedPriceRangeLow, setExpeditedPriceRangeLow] = useState('');
  const [expeditedPriceRangeHigh, setExpeditedPriceRangeHigh] = useState('');
  const [superExpeditedPriceRangeLow, setSuperExpeditedPriceRangeLow] = useState('');
  const [superExpeditedPriceRangeHigh, setSuperExpeditedPriceRangeHigh] = useState('');
  const [standardQuotePayload, setStandardQuotePayload] = useState(null);
  const [expeditedQuotePayload, setExpeditedQuotePayload] = useState(null);
  const [superExpeditedQuotePayload, setSuperExpeditedQuotePayload] = useState(null);

  //! Invoice Billing for Gophr Store
  const [isInvoice, setIsInvoice] = useState(false);
  const [isInvoiceDisabled, setIsInvoiceDisabled] = useState(false);
  const [billingInfo, setBillingInfo] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [billingErrors, setBillingErrors] = useState({
    billingFirstname: false,
    billingLastname: false,
    billingPhone: false,
    billingEmail: false
  });

  //! TQL related - for quote_with_rate endpoint
  const [hasTqlRateValue, setHasTqlRateValue] = useState(false);
  const [tqlRateBlob, setTqlRateBlob] = useState({});
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('company_terms');
  const [paymentProfileState, setPaymentProfileState] = useState({
    loaded: false,
    fetchFailed: false,
    allowsNetTerms: false,
    hasSavedPaymentMethods: false,
    invoiceFrequency: 'single',
    billingAuth0Id: null,
    netTermsBillingAuth0Id: null,
    cardBillingAuth0Id: null,
    selectedCardBillingAuth0Id: null,
  });

  useEffect(() => {
    if (requiresRangeQuoteForVehicleType(vehicleType)) {
      setIsInvoice(false);
      setIsInvoiceDisabled(true);
    } else {
      setIsInvoiceDisabled(false);
    }
  }, [vehicleType]);

  useEffect(() => {
    if (selectedStore && selectedType) {
      setShowForm1(true);
      setShowForm3(false);
    }
  }, [selectedStore, selectedType]);

  useEffect(() => {
    if ((editingSavedShipmentId || editingActiveShipmentId) && formTopRef.current && typeof formTopRef.current.scrollIntoView === 'function') {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingSavedShipmentId, editingActiveShipmentId]);

  useEffect(() => {
    if (selectedStore) {
      const primaryOwner = getPreferredStoreBillingOwner(selectedStore.StoreOwnerships);
      const selectedStoreLatitude = toSafeString(selectedStore.latitude);
      const selectedStoreLongitude = toSafeString(selectedStore.longitude);

      setVendorLat(selectedStoreLatitude);
      setVendorLong(selectedStoreLongitude);
      setStoreAddress1(selectedStore.address_1);
      setStoreAddress2(selectedStore.address_2);
      setStoreCity(selectedStore.city);
      setStoreCountry(selectedStore.country);
      setStoreName(selectedStore.name);
      setStoreId(selectedStore.store_id);
      setStoreZip(selectedStore.zip != null ? selectedStore.zip.toString() : '');
      setStoreFirstName(primaryOwner ? primaryOwner.firstname : null);
      setStoreLastName(primaryOwner ? primaryOwner.lastname : null);
      setStorePhone(selectedStore.phone);
      setStoreState(selectedStore.state);
      setStoreAddress(selectedStore.address_1 + ', ' + selectedStore.city + ', ' + selectedStore.state + ' ' + selectedStore.zip + ', ' + selectedStore.country);
      setStoreLat(selectedStoreLatitude);
      setStoreLng(selectedStoreLongitude);
      setSellsAlcohol(selectedStore.sells_alcohol);
      setMerchantAuth0ID(primaryOwner ? primaryOwner.merchant_id : '');
      setStoreMarketId(selectedStore.market_id);
      setIsInvoice(false);
      setSelectedPaymentMethodId(null);
      setSelectedPaymentMode('company_terms');
      setStandardQuotePayload(null);
      setExpeditedQuotePayload(null);
      setSuperExpeditedQuotePayload(null);
      setPaymentProfileState({
        loaded: false,
        fetchFailed: false,
        allowsNetTerms: false,
        hasSavedPaymentMethods: false,
        invoiceFrequency: 'single',
        billingAuth0Id: null,
        netTermsBillingAuth0Id: null,
        cardBillingAuth0Id: null,
        selectedCardBillingAuth0Id: null,
      });
    }
  }, [selectedStore]);

  const selectedStoreBillingOwner = getPreferredStoreBillingOwner(selectedStore && selectedStore.StoreOwnerships);

  useEffect(() => {
    const draftState = location && location.state && location.state.savedDraftEdit ? location.state.savedDraftEdit : null;
    const activeState = location && location.state && location.state.activeOrderEdit ? location.state.activeOrderEdit : null;
    const editState = draftState || activeState;
    const isActiveEdit = !draftState && Boolean(activeState);
    const draftShipment = editState && editState.shipment ? editState.shipment : null;

    if (!draftShipment || !draftShipment.shipment_id || draftHydratedShipmentIdRef.current === draftShipment.shipment_id) {
      return;
    }

    draftHydratedShipmentIdRef.current = draftShipment.shipment_id;

    const editableActiveTypes = ['direct', 'routed_direct', 'multidrop_direct'];
    const shipmentType = draftShipment.shipment_type || 'direct';
    if (isActiveEdit) {
      if (!editableActiveTypes.includes(shipmentType)) {
        notify('tc', null, 'This order type cannot be edited here.');
        return;
      }
    } else if (shipmentType !== 'direct') {
      notify('tc', null, 'Only regular direct saved drafts can be reopened here.');
      return;
    }

    let active = true;

    const hydrateSavedDraft = async () => {
      const draftStoreId = editState.storeId || resolveDraftStoreId(draftShipment);

      if (!draftStoreId) {
        notify('tc', null, isActiveEdit ? 'This order is missing its store association.' : 'This saved draft is missing its store association.');
        return;
      }

      const storeResponse = await runWithSessionRecovery(() =>
        axios({
          method: 'post',
          url: inventory_urls.hasuraHost,
          headers: {
            Authorization: buildAuthorizationHeader(),
            'content-type': 'application/json'
          },
          data: {
            query: `{
              Stores(where: {store_id: {_eq: "${draftStoreId}"}}) {
                store_id
                name
                phone
                address_1
                address_2
                city
                state
                zip
                country
                latitude
                longitude
                market_id
                sells_alcohol
                StoreOwnerships {
                  firstname
                  lastname
                  email
                  merchant_id
                  role
                }
              }
            }`
          },
        })
      );

      if (!active) {
        return;
      }

      const draftStore =
        storeResponse && storeResponse.data && storeResponse.data.data && Array.isArray(storeResponse.data.data.Stores)
          ? storeResponse.data.data.Stores[0]
          : null;

      if (!draftStore) {
        notify('tc', null, isActiveEdit ? 'Store details were not found for this order.' : 'Store details were not found for this saved draft.');
        return;
      }

      const pickupOrder = getDraftOrderByAction(draftShipment, 'pick_up', 0);
      const dropoffOrder = getDraftOrderByAction(draftShipment, 'drop_off', 1);
      const priceRange = draftShipment.shipping_fee_blob && draftShipment.shipping_fee_blob.calculated && draftShipment.shipping_fee_blob.calculated.shipping_fee_range;
      const resolvedQuote =
        draftShipment.quote != null
          ? Number(draftShipment.quote)
          : draftShipment.shipping_fee != null
          ? Number(draftShipment.shipping_fee)
          : draftShipment.shipping_fee_blob && draftShipment.shipping_fee_blob.calculated
          ? Number(draftShipment.shipping_fee_blob.calculated.shipping_fee)
          : null;

      const parseItems = order => (isActiveEdit ? parseOrderItemsForForm(order, draftShipment.description) : parseDraftItems(order, draftShipment.description));
      const shipmentOrders = Array.isArray(draftShipment.Orders) ? draftShipment.Orders.filter(Boolean) : [];

      // Populate field state BEFORE mounting the form. RegularCreation seeds its address inputs
      // from pickupInfo/dropoffInfo only once at mount, so the locations must already be set when
      // it mounts (store/type are selected last) or the dropoff address stays blank.
      let selectedFormType = 'regular';

      if (shipmentType === 'routed_direct') {
        selectedFormType = 'routed';
        const pick = shipmentOrders[0] || pickupOrder;
        const drop = shipmentOrders[shipmentOrders.length - 1] || dropoffOrder;
        setPickupInfo((pick && pick.location_blob) || {});
        setDropoffInfo((drop && drop.location_blob) || {});
        setPickupInstruction((pick && pick.pick_up_instructions) || '');
        setDropoffInstruction((drop && drop.drop_off_instructions) || 'N/A');
        setPickupItems(parseItems(pick));
        setDropoffItems(parseItems(drop));
        // Remember which existing Order each stop maps to so the backend can add/remove stops by id.
        const capturedOrderIds = {
          pickupOrderId: (pick && pick.order_id) || null,
          dropoffOrderId: (drop && drop.order_id) || null,
        };
        if (shipmentOrders.length >= 4) {
          const waypointDrop = shipmentOrders[1];
          const waypointPick = shipmentOrders[2];
          setIsWaypointDropoff(true);
          setIsWaypointPickup(true);
          setWaypointInfo((waypointDrop && waypointDrop.location_blob) || (waypointPick && waypointPick.location_blob) || {});
          setWaypointDropoffInstruction((waypointDrop && waypointDrop.drop_off_instructions) || '');
          setWaypointPickupInstruction((waypointPick && waypointPick.pick_up_instructions) || '');
          setWaypointDropoffItems(parseItems(waypointDrop));
          setWaypointPickupItems(parseItems(waypointPick));
          capturedOrderIds.waypointDropoffOrderId = (waypointDrop && waypointDrop.order_id) || null;
          capturedOrderIds.waypointPickupOrderId = (waypointPick && waypointPick.order_id) || null;
        } else if (shipmentOrders.length === 3) {
          const mid = shipmentOrders[1];
          if (orderHasAction(mid, 'drop_off')) {
            setIsWaypointDropoff(true);
            setIsWaypointPickup(false);
            setWaypointInfo((mid && mid.location_blob) || {});
            setWaypointDropoffInstruction((mid && mid.drop_off_instructions) || '');
            setWaypointDropoffItems(parseItems(mid));
            capturedOrderIds.waypointDropoffOrderId = (mid && mid.order_id) || null;
          } else {
            setIsWaypointPickup(true);
            setIsWaypointDropoff(false);
            setWaypointInfo((mid && mid.location_blob) || {});
            setWaypointPickupInstruction((mid && mid.pick_up_instructions) || '');
            setWaypointPickupItems(parseItems(mid));
            capturedOrderIds.waypointPickupOrderId = (mid && mid.order_id) || null;
          }
        }
        setEditOrderIds(capturedOrderIds);
      } else if (shipmentType === 'multidrop_direct') {
        // Dispatch edits open in the routed screen so a pickup and/or dropoff waypoint can be added.
        selectedFormType = 'itemized-multidrop';
        const pick = shipmentOrders[0] || pickupOrder;
        const drops = shipmentOrders.slice(1);
        setPickupInfo((pick && pick.location_blob) || {});
        setPickupInstruction((pick && pick.pick_up_instructions) || '');
        setDropoffInstruction('N/A');
        setPickupItems(parseItems(pick));
        const waypointsArray = drops.map(dropOrder => {
          const blob = (dropOrder && dropOrder.location_blob) || {};
          return {
            waypointFirstname: blob.first_name || '',
            waypointLastname: blob.last_name || '',
            waypointPhone: blob.phone || '',
            waypointAddress1: blob.address_1 || null,
            waypointAddress2: blob.address_2 || null,
            waypointFullAddress: blob.address || '',
            waypointLat: blob.lat || '',
            waypointLng: blob.lng || '',
            waypointCity: blob.city || '',
            waypointState: blob.state || '',
            waypointCountry: blob.country || '',
            waypointZip: blob.zip || '',
            waypointDropoffInstruction: (dropOrder && dropOrder.drop_off_instructions) || '',
            waypointBusinessName: blob.business_name || '',
            waypointCustomerSuggestions: [],
            isWaypointAutoFilled: false,
            dropItems: parseItems(dropOrder),
            dropItemsError: [false],
          };
        });
        if (waypointsArray.length > 0) {
          setMultiDropWaypointsArray(waypointsArray);
          setMultiDropWaypointsErrors(
            waypointsArray.map(() => ({
              waypointFirstname: false,
              waypointLastname: false,
              waypointPhone: false,
              waypointAddress: false,
            })),
          );
          setMultiDropsInfo(drops.map(dropOrder => (dropOrder && dropOrder.location_blob) || {}));
        }
      } else {
        setPickupInfo((pickupOrder && pickupOrder.location_blob) || {});
        setDropoffInfo((dropoffOrder && dropoffOrder.location_blob) || {});
        setPickupInstruction((pickupOrder && pickupOrder.pick_up_instructions) || draftShipment.pick_up_instructions || '');
        setDropoffInstruction((dropoffOrder && dropoffOrder.drop_off_instructions) || draftShipment.drop_off_instructions || '');
        setDefaultItems(parseItems(pickupOrder || dropoffOrder));
      }
      setMerchantOrderID(draftShipment.merchant_order_id || null);
      setVehicleType(normalizeShipmentVehicleTypeForApi(draftShipment.vehicle_type || ''));
      setNumPeople(draftShipment.num_of_gophrs || null);
      setWeight(draftShipment.weight || '');
      setIsSaved(!isActiveEdit);
      setIsScheduled(Boolean(draftShipment.scheduled_for));
      setScheduledFor(draftShipment.scheduled_for ? new Date(draftShipment.scheduled_for) : null);
      setIsExpedited(Boolean(draftShipment.is_expedited));
      setIsSuperExpedited(Boolean(draftShipment.is_super_expedited));
      setDistance(draftShipment.distance || '');
      setPrice(Number.isFinite(resolvedQuote) ? resolvedQuote : null);
      // Remember the order's price at open time so checkout can show original vs new + the difference.
      setOriginalPrice(isActiveEdit && Number.isFinite(resolvedQuote) ? Number(resolvedQuote) : null);
      setPriceRangeLow(priceRange && Number.isFinite(Number(priceRange.min)) ? Number(priceRange.min) : Number.isFinite(resolvedQuote) ? resolvedQuote : null);
      setPriceRangeHigh(priceRange && Number.isFinite(Number(priceRange.max)) ? Number(priceRange.max) : Number.isFinite(resolvedQuote) ? resolvedQuote : null);
      setRoute(buildDraftRouteSnapshot(draftShipment));
      setIsForkLift(Boolean(draftShipment.is_fork_lift));
      setIsDolly(Boolean(draftShipment.is_dolly));
      setIsHandLoadUnload(Boolean(draftShipment.is_hand_loading));
      setIsHazmat(Boolean(draftShipment.is_hazmat));
      setIsTWICCard(Boolean(draftShipment.is_twic_card));
      setIsLiftgate(Boolean(draftShipment.is_liftgate));
      setIsPalletJack(Boolean(draftShipment.is_pallet_jack));
      setIsWeatherProtection(Boolean(draftShipment.is_weather_protection));
      setIsMedical(Boolean(draftShipment.is_medical));
      if (isActiveEdit) {
        setEditingActiveShipmentId(draftShipment.shipment_id);
      } else {
        setEditingSavedShipmentId(draftShipment.shipment_id);
      }

      // Dispatch edits reuse the real "Create a Delivery" (DropshipForm) screen, prefilled from the
      // order, with the normal checkout/payment terms. DropshipForm hydrates itself from the order.
      if (isActiveEdit) {
        selectedFormType = 'multidrop';
        setActiveEditShipment(draftShipment);
      }

      // Mount the create form last, with locations already populated so the address inputs seed.
      setSelectedType(selectedFormType);
      setSelectedStore(draftStore);
      setShowFormSelection(false);
      setShowForm1(true);
      setShowForm2(false);
      setShowForm3(false);
    };

    hydrateSavedDraft().catch(() => {
      if (active) {
        notify('tc', null, isActiveEdit ? 'Unable to open this order for editing right now.' : 'Unable to reopen the saved draft right now.');
      }
    });

    return () => {
      active = false;
    };
  }, [location]);

  // Prefill from an abandoned-cart lead (CRM "Create order"). These are consumer `direct` quotes
  // with no store/addresses/payment, so we only seed the store-independent fields (vehicle/schedule/
  // accessorials/weight/quoted fee) and surface the customer + reverse-geocoded addresses in a banner
  // for the dispatcher to place. The dispatcher still picks the billing store and confirms addresses.
  useEffect(() => {
    const prefill = location && location.state && location.state.abandonedPrefill ? location.state.abandonedPrefill : null;
    if (!prefill || abandonedPrefillAppliedRef.current === prefill.lead_id) {
      return;
    }
    abandonedPrefillAppliedRef.current = prefill.lead_id;

    if (prefill.vehicle_type) {
      setVehicleType(normalizeShipmentVehicleTypeForApi(prefill.vehicle_type));
    }
    if (prefill.num_of_gophrs != null) {
      setNumPeople(prefill.num_of_gophrs);
    }
    if (prefill.weight != null && prefill.weight !== '') {
      setWeight(prefill.weight);
    }
    if (prefill.scheduled_for) {
      setIsScheduled(true);
      setScheduledFor(new Date(prefill.scheduled_for));
    }
    if (prefill.quoted_fee != null) {
      setPrice(Number(prefill.quoted_fee));
    }
    const acc = prefill.accessorials || {};
    setIsExpedited(Boolean(acc.is_expedited));
    setIsSuperExpedited(Boolean(acc.is_super_expedited));
    setIsRoundTrip(Boolean(acc.is_roundtrip));
    setIsForkLift(Boolean(acc.is_fork_lift));
    setIsDolly(Boolean(acc.is_dolly));
    setIsHazmat(Boolean(acc.is_hazmat));
    setIsPalletJack(Boolean(acc.is_pallet_jack));
    setIsTWICCard(Boolean(acc.is_twic_card));
    setIsLiftgate(Boolean(acc.is_liftgate));
    setIsHandLoadUnload(Boolean(acc.is_hand_loading));
    setIsWeatherProtection(Boolean(acc.is_weather_protection));
    setIsMedical(Boolean(acc.is_medical));

    setAbandonedPrefillBanner(prefill);

    // If the customer resolves to a merchant store, auto-select it so the dispatcher skips the
    // store picker — the [selectedStore] effect populates all store/billing fields. The create form
    // mounts straight into the regular direct flow with this merchant on behalf.
    if (prefill.store && prefill.store.store_id) {
      setSelectedStore(prefill.store);
      setSelectedType('multidrop');
      setShowFormSelection(false);
      setShowForm1(true);
      setShowForm2(false);
      setShowForm3(false);
    }

    // Clear navigation state so a refresh/back doesn't re-apply the prefill.
    if (history && history.replace) {
      history.replace(location.pathname);
    }
  }, [location, history]);

  const validatePhone = phone => {
    var phoneRex = /((?:\+|00)[17](?: |-)?|(?:\+|00)[1-9]\d{0,2}(?: |-)?|(?:\+|00)1-\d{3}(?: |-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |-)[0-9]{3}(?: |-)[0-9]{4})|([0-9]{10}))/;

    if (phone.replace(/[^0-9]/g, '').length > 9 && phone.replace(/[^0-9]/g, '').length < 14) {
      return phoneRex.test(String(phone));
    }
  };

  // function that returns true if value is a valid email, false otherwise
  const validateEmail = email => {
    var emailRex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRex.test(String(email.toLowerCase().trim()));
  };

  const checkInvoice = () => {
    let updatedBillingErrors = billingErrors;

    if (billingInfo.first_name) {
      updatedBillingErrors.billingFirstname = false;
    } else {
      updatedBillingErrors.billingFirstname = true;
    }
    if (billingInfo.last_name) {
      updatedBillingErrors.billingLastname = false;
    } else {
      updatedBillingErrors.billingLastname = true;
    }
    if (validatePhone(billingInfo.phone)) {
      updatedBillingErrors.billingPhone = false;
    } else {
      updatedBillingErrors.billingPhone = true;
    }
    if (validateEmail(billingInfo.email)) {
      updatedBillingErrors.billingEmail = false;
    } else {
      updatedBillingErrors.billingEmail = true;
    }
    setBillingErrors(updatedBillingErrors);
    return Object.values(billingErrors).includes(true);
  };

  const notify = (place, errorType, customMessage) => {
    var type = 'info';
    var options = {
      place: place,
      message: (
        <div>
          <div>{errorType === null ? customMessage : errorType === 'distance' ? errorMessageDistance.errorMessage : errorMessageSubmit.errorMessage}</div>
        </div>
      ),
      type: type,
      icon: 'tim-icons icon-alert-circle-exc',
      autoDismiss: 10
    };
    if (notificationAlertRef.current) {
      notificationAlertRef.current.notificationAlert(options);
    }
  };

  const handleErrorSubmit = error => {
    setErrorSubmit(error);
  };

  const blockPaymentDependentSubmission = errorMessage => {
    const submissionError = {
      errorMessage,
      errorCode: 10,
    };

    handleErrorSubmit(submissionError);
    setErrorMessageSubmit(submissionError);
    notify('tc', null, errorMessage);
  };

  const flattenSubmissionError = value => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      return value
        .map(flattenSubmissionError)
        .filter(Boolean)
        .join(' ');
    }

    if (typeof value === 'object') {
      return Object.values(value)
        .map(flattenSubmissionError)
        .filter(Boolean)
        .join(' ');
    }

    return String(value);
  };

  const buildSubmissionError = error => {
    const fallbackMessage = 'Something went wrong with your submission. Please wait a few seconds and try again.';

    if (!error || !error.response) {
      return {
        errorMessage: fallbackMessage,
        errorCode: 10
      };
    }

    const { data } = error.response;
    const primaryMessage = flattenSubmissionError(data && (data.message || data.error));
    const detailMessage = flattenSubmissionError(data && (data.details || data.errors));
    const combinedMessage = [primaryMessage, detailMessage]
      .filter(Boolean)
      .filter((message, index, messages) => messages.indexOf(message) === index)
      .join(' ');

    return {
      errorMessage: combinedMessage || fallbackMessage,
      errorCode: 10
    };
  };

  const handleBack = () => {
    setShowFormSelection(true);
    setShowForm1(true);
    setShowForm2(true);
    setShowForm3(false);
  };

  const postShipmentWithFallback = ({ url, data, method = 'post' }) => {
    // Attach the optional preferred driver to every create flow (regular/routed/multidrop/itemized).
    const submissionData = preferredDriver && preferredDriver.driver_auth0_id
      ? { ...data, preferred_driver: preferredDriver }
      : data;
    const executeRequest = config =>
      runWithSessionRecovery(() =>
        axios({
          ...config,
          headers: {
            ...(config.headers || {}),
            Authorization: buildAuthorizationHeader(),
          },
        })
      );

    const requestConfig = {
      method,
      withCredentials: true,
      headers: { 'content-type': 'application/json' },
      data: submissionData,
    };

    const hostedUrl = shipment_urls.toHostedShipmentUrl(url);
    const isScheduledWithRateUrl = typeof url === 'string' && url.includes('/create_with_rate/schedule');
    // Never fall back to the legacy /create endpoint for _with_rate orders — the old and new
    // calculators diverge significantly (~30%), causing guaranteed QuoteDiffErrors on every retry.
    const legacyUrl = null;
    const hostedLegacyUrl = null;

    const fallbackUrls = (isScheduledWithRateUrl
      ? [hostedUrl]
      : [hostedUrl])
      .filter(Boolean)
      .filter((candidate, index, urls) => candidate !== url && urls.indexOf(candidate) === index);

    const shouldAllowAuthFallbackFromLocal = status =>
      (status === 401 || status === 403) && typeof url === 'string' && isLocalLikeHost(url);

    const shouldRetryHosted = error => {
      const status = error && error.response ? error.response.status : null;

      if (fallbackUrls.length === 0) {
        return false;
      }

      if (status === 401 || status === 403) {
        return shouldAllowAuthFallbackFromLocal(status);
      }

      if (status === null) {
        return true;
      }

      return status >= 400;
    };

    const now = Date.now();
    const shouldSkipLocalCreate = localShipmentCreateRetryAtRef.current > now;

    if (shouldSkipLocalCreate && fallbackUrls.length > 0) {
      return fallbackUrls.reduce((promise, candidateUrl, index) => {
        return promise.catch(error => {
          const status = error && error.response ? error.response.status : null;
          if (index > 0 && (status === 401 || status === 403)) {
            throw error;
          }

          return executeRequest({
            ...requestConfig,
            url: candidateUrl,
          });
        });
      }, executeRequest({
        ...requestConfig,
        url: fallbackUrls[0],
      }));
    }

    return fallbackUrls.reduce((promise, candidateUrl, index) => {
      return promise.catch(error => {
        if (index === 0) {
          if (!shouldRetryHosted(error)) {
            throw error;
          }

          const status = error && error.response ? error.response.status : null;
          if (status === null || status === 404 || status >= 500) {
            localShipmentCreateRetryAtRef.current = Date.now() + LOCAL_CREATE_RETRY_COOLDOWN_MS;
          }
        } else {
          const status = error && error.response ? error.response.status : null;
          if (status === 401 || status === 403) {
            throw error;
          }
        }

        return executeRequest({
          ...requestConfig,
          url: candidateUrl,
        });
      });
    }, executeRequest({
      ...requestConfig,
      url,
    }));
  };

  const buildRangePayload = (low, high) => {
    const min = Number(low);
    const max = Number(high);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }

    return { min, max };
  };

  const buildSubmittedQuotePayload = () => {
    const regularRange = buildRangePayload(priceRangeLow, priceRangeHigh);
    const standardRange = buildRangePayload(standardPriceRangeLow, standardPriceRangeHigh) || regularRange;
    const expeditedRange = buildRangePayload(expeditedPriceRangeLow, expeditedPriceRangeHigh);
    const superExpeditedRange = buildRangePayload(superExpeditedPriceRangeLow, superExpeditedPriceRangeHigh);
    const activeRange = isSuperExpedited
      ? superExpeditedRange || expeditedRange || standardRange
      : isExpedited
      ? expeditedRange || standardRange
      : regularRange || standardRange;

    if (!activeRange) {
      return null;
    }

    return {
      shipping_fee_range: activeRange,
      ...(standardRange ? { shipping_fee_range_standard: standardRange } : {}),
      ...(expeditedRange ? { shipping_fee_range_expedited: expeditedRange } : {}),
      ...(superExpeditedRange ? { shipping_fee_range_super_expedited: superExpeditedRange } : {}),
        ...(activeQuoteLineItems.length > 0 ? { line_items: activeQuoteLineItems } : {}),
    };
  };

  const getQuoteLineItems = quotePayload => {
    const quote = quotePayload && typeof quotePayload === 'object' ? quotePayload : {};
    const lineItemDefinitions = [
      { selected: isTWICCard, label: 'TWIC Card', feeKey: 'twic_card_fee' },
      { selected: isLiftgate, label: 'Lift Gate', feeKey: 'liftgate_fee' },
      { selected: isWeatherProtection, label: 'Weather Protection', feeKey: 'weather_protection_fee' },
      { selected: isHazmat, label: 'Hazmat', feeKey: 'hazmat_fee' },
      { selected: isPalletJack, label: 'Pallet Jack', feeKey: 'pallet_jack_fee' },
      { selected: isDolly, label: 'Dolly', feeKey: 'dolly_fee' },
      { selected: isHandLoadUnload, label: 'Hand Loading', feeKey: 'hand_loading_fee' },
      { selected: isMedical, label: 'Medical Equipment', feeKey: 'medical_fee' },
    ];

    return lineItemDefinitions
      .filter(item => item.selected)
      .map(item => ({
        code: item.feeKey,
        label: item.label,
        amount: toFiniteCurrency(quote[item.feeKey]),
      }));
  };

  const activeQuoteLineItems = getQuoteLineItems(
    isSuperExpedited
      ? superExpeditedQuotePayload || expeditedQuotePayload || standardQuotePayload
      : isExpedited
      ? expeditedQuotePayload || standardQuotePayload
      : standardQuotePayload,
  );
  const activeQuotePayload = isSuperExpedited
    ? superExpeditedQuotePayload || expeditedQuotePayload || standardQuotePayload
    : isExpedited
    ? expeditedQuotePayload || standardQuotePayload
    : standardQuotePayload;

  const refreshRegularQuoteBeforeSubmit = async submissionData => {
    if (!submissionData || !Array.isArray(submissionData.orders) || submissionData.orders.length === 0) {
      return submissionData;
    }

    const hostedQuoteUrl = shipment_urls.toHostedShipmentUrl(shipment_urls.getShipmentQuoteWithRate);
    const quotePayload = {
      market_id: submissionData.market_id,
      quoted_time: submissionData.quoted_time,
      distance: Number(submissionData.distance),
      weight: Number(submissionData.weight),
      vehicle_type: submissionData.vehicle_type,
      num_of_gophrs: submissionData.num_of_gophrs,
      is_expedited: submissionData.is_expedited === true || submissionData.is_expedited === 'true',
      is_super_expedited: submissionData.is_super_expedited === true || submissionData.is_super_expedited === 'true',
      is_roundtrip: Boolean(submissionData.is_roundtrip),
      contains_alcohol: false,
      order_points: submissionData.orders.map(order => [order.location_blob.lat, order.location_blob.lng]),
      num_of_drops: submissionData.num_of_drops,
      is_fork_lift: Boolean(submissionData.is_fork_lift),
      is_dolly: Boolean(submissionData.is_dolly),
      is_hazmat: Boolean(submissionData.is_hazmat),
      is_pallet_jack: Boolean(submissionData.is_pallet_jack),
      is_twic_card: Boolean(submissionData.is_twic_card),
      is_liftgate: Boolean(submissionData.is_liftgate),
      is_hand_loading: Boolean(submissionData.is_hand_loading),
      is_weather_protection: Boolean(submissionData.is_weather_protection),
      is_medical: Boolean(submissionData.is_medical),
      temperature: 20,
      scheduled_for: submissionData.scheduled_for,
      scheduled_for_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      load_value: '36000',
      stops: [
        {
          stopType: 'PICK',
          stopOrder: 1,
          postalCode: pickupInfo.zip || '',
          countryCode: pickupInfo.country === 'Canada' ? 'CA' : 'US'
        },
        {
          stopType: 'DROP',
          stopOrder: 2,
          postalCode: dropoffInfo.zip || '',
          countryCode: dropoffInfo.country === 'Canada' ? 'CA' : 'US'
        }
      ],
      commodity: (defaultItems[0] && defaultItems[0].detail) || 'items',
      do_tql_cal: false,
      is_over_size: Boolean(submissionData.is_over_size),
      drop_off_state: submissionData.drop_off_state || '',
      is_market_base_quote: Boolean(submissionData.is_market_base_quote)
    };

    const response = await runWithSessionRecovery(() =>
      axios({
        method: 'post',
        url: hostedQuoteUrl,
        withCredentials: true,
        headers: { 'content-type': 'application/json', Authorization: buildAuthorizationHeader() },
        data: quotePayload,
      })
    );

    const payload = response && response.data && response.data.payload;
    const refreshedQuote = payload && payload.shipping_fee;

    if (refreshedQuote == null) {
      return submissionData;
    }

    return {
      ...submissionData,
      quote: String(refreshedQuote),
    };
  };

  const getDeferredBillingPayload = ({ shipmentStatus } = {}) => {
    const isQuoteOnlyRequest =
      (((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) ||
        vehicleType === 'car_hauler');
    const isSaveForLaterRequest = shipmentStatus === 'shipment_saved';
    const shouldForceDirectInvoice = Boolean(isInvoice && billingInfo && billingInfo.first_name && billingInfo.first_name.trim() !== '');
    const cachedStoreInvoiceFrequency = storeId ? getStoredStoreInvoiceFrequency(storeId) : null;
    const normalizedPaymentProfileInvoiceFrequency = normalizeCompanyPaymentTerm(paymentProfileState.invoiceFrequency);
    const normalizedCachedStoreInvoiceFrequency = normalizeCompanyPaymentTerm(cachedStoreInvoiceFrequency);
    const shouldPreferStoreScopedInvoiceFrequency =
      Boolean(normalizedCachedStoreInvoiceFrequency)
      && normalizedCachedStoreInvoiceFrequency !== 'single'
      && normalizedPaymentProfileInvoiceFrequency === 'single';
    const effectiveInvoiceFrequency =
      shouldPreferStoreScopedInvoiceFrequency
        ? normalizedCachedStoreInvoiceFrequency
        : normalizedPaymentProfileInvoiceFrequency && (paymentProfileState.loaded || normalizedPaymentProfileInvoiceFrequency !== 'single')
        ? normalizedPaymentProfileInvoiceFrequency
        : (normalizedCachedStoreInvoiceFrequency || normalizedPaymentProfileInvoiceFrequency || 'single');
    const paymentTermPolicy = getCompanyPaymentTermPolicy({
      paymentTerm: effectiveInvoiceFrequency,
      isQuoteOnly: isQuoteOnlyRequest,
      isAdminOnBehalf: true,
    });
    const effectiveSelectedPaymentMethodId =
      selectedPaymentMethodId || paymentProfileState.selectedPaymentMethodId || paymentProfileState.defaultPaymentMethodId || null;
    const normalizedPaymentMode = shouldForceDirectInvoice
      ? 'card'
      : paymentTermPolicy.isPrepay
      ? 'card'
      : (selectedPaymentMode === 'card' ? 'card' : 'net_terms');
    const normalizedInvoiceFrequency = shouldForceDirectInvoice ? 'single' : paymentTermPolicy.normalizedTerm;
    const billingAuth0Id = merchantAuth0ID || (
      normalizedPaymentMode === 'net_terms'
        ? (paymentProfileState.netTermsBillingAuth0Id || paymentProfileState.billingAuth0Id || SUB)
        : (paymentProfileState.billingAuth0Id || SUB)
    );
    const selectedCardBillingAuth0Id = normalizedPaymentMode === 'card' && effectiveSelectedPaymentMethodId
      ? (paymentProfileState.selectedCardBillingAuth0Id || paymentProfileState.cardBillingAuth0Id || merchantAuth0ID || null)
      : null;

    if (!isQuoteOnlyRequest && !isSaveForLaterRequest) {
      if (!paymentProfileState.loaded) {
        return {
          errorMessage: 'Payment options are still loading. Please wait a moment and try again.',
        };
      }

      if (paymentProfileState.fetchFailed) {
        return {
          errorMessage: 'Unable to load payment options for this store. Refresh the page and try again.',
        };
      }

      if (paymentTermPolicy.requiresSavedCardOnFile && !effectiveSelectedPaymentMethodId && !paymentTermPolicy.allowsPendingWithoutCard) {
        return {
          errorMessage: 'This store requires a saved card before you can submit the order. Add or select a card and try again.',
        };
      }

      if (normalizedPaymentMode === 'card' && !effectiveSelectedPaymentMethodId && !paymentTermPolicy.allowsPendingWithoutCard) {
        return {
          errorMessage: 'Select a saved card or add a card before submitting this order.',
        };
      }

      if (normalizedPaymentMode === 'card' && !selectedCardBillingAuth0Id) {
        return {
          errorMessage: 'The selected card is still syncing with the checkout profile. Wait a moment, reselect the card, and try again.',
        };
      }

      if (normalizedPaymentMode === 'net_terms' && !paymentTermPolicy.allowsCompanyTermsSelection) {
        return {
          errorMessage: 'Company payment terms could not be verified for this store. Refresh the page and confirm the payment profile before submitting.',
        };
      }
    }

    return {
      errorMessage: null,
      selected_payment_mode: isQuoteOnlyRequest ? 'quote_required' : normalizedPaymentMode,
      selected_invoice_frequency: normalizedInvoiceFrequency,
      payment_method_id: normalizedPaymentMode === 'card' ? effectiveSelectedPaymentMethodId : null,
      bill_to_auth0_id: billingAuth0Id,
      bill_to_card_auth0_id: selectedCardBillingAuth0Id,
      bill_to_store_merchant_auth0_id: merchantAuth0ID || null,
      bill_to_store_id: storeId || null,
      company_payment_terms: normalizedInvoiceFrequency,
      payment_collection_type: shouldForceDirectInvoice ? 'immediate' : paymentTermPolicy.collectionType,
    };
  };

  const handleConfirmRegular = (shipmentStatus, orderStatus, isScheduled) => {
    let url;
    let method = 'post';
    let scheduledForData = null;
    const deferredBillingPayload = getDeferredBillingPayload({ shipmentStatus });
    const shouldBypassScheduledWithRateForCompanyTerms = false;

    if (deferredBillingPayload.errorMessage) {
      blockPaymentDependentSubmission(deferredBillingPayload.errorMessage);
      return;
    }

    if (dropoffInstruction === null || dropoffInstruction === '') {
      setDropoffInstruction('N/A');
    }
    if (scheduledFor) {
      scheduledForData = scheduledFor.toISOString();
    }
    if (editingActiveShipmentId) {
      url = shipment_urls.updateActiveShipmentWithRate(editingActiveShipmentId);
      method = 'put';
    } else if (editingSavedShipmentId) {
      url = shipment_urls.updateSavedShipmentWithRate(editingSavedShipmentId);
      method = 'put';
    } else if (isScheduled) {
      url = shouldBypassScheduledWithRateForCompanyTerms
        ? shipment_urls.createScheduledShipment
        : shipment_urls.createScheduledShipmentWithRate;
    } else {
      if (shipmentStatus === 'shipment_saved') {
        url = shipment_urls.createSavedShipmentWithRate;
      } else {
        url = shipment_urls.createShipmentWithRate;
      }
    }
    let pickOrder = {
      store_id: storeId,
      // Pickup location
      location_blob: {
        name: pickupInfo.name,
        first_name: pickupInfo.first_name,
        last_name: pickupInfo.last_name,
        phone: pickupInfo.phone,
        address: pickupInfo.address,
        address_1: pickupInfo.address_1,
        address_2: pickupInfo.address_2,
        city: pickupInfo.city,
        zip: pickupInfo.zip,
        state: pickupInfo.state,
        country: 'US',
        business_name: pickupInfo.business_name ? pickupInfo.business_name : '',
        lat: pickupInfo.lat,
        lng: pickupInfo.lng
      },
      weight: weight,
      description: JSON.stringify(defaultItems),
      pick_up_instructions: pickupInstruction,
      actions: {
        pick_up: defaultItems
      }
    };
    let dropOrder = {
      store_id: storeId,
      // Dropoff location
      location_blob: {
        name: dropoffInfo.name,
        first_name: dropoffInfo.first_name,
        last_name: dropoffInfo.last_name,
        phone: dropoffInfo.phone,
        address: dropoffInfo.address,
        address_1: dropoffInfo.address_1,
        address_2: dropoffInfo.address_2,
        city: dropoffInfo.city,
        zip: dropoffInfo.zip,
        state: dropoffInfo.state,
        country: 'US',
        business_name: dropoffInfo.business_name ? dropoffInfo.business_name : '',
        lat: dropoffInfo.lat,
        lng: dropoffInfo.lng
      },
      weight: weight,
      description: JSON.stringify(defaultItems),
      drop_off_instructions: dropoffInstruction,
      actions: {
        drop_off: defaultItems
      }
    };

    let ordersData = [pickOrder, dropOrder];

    let itemNum = 0;
    defaultItems.forEach(it => {
      itemNum += it.quantity;
    });
    let data = {
      status: shipmentStatus,
      market_id: storeMarketId,
      scheduled_for: scheduledForData,
      weight: weight,
      vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
      is_expedited: isExpedited.toString(),
      is_super_expedited: isSuperExpedited,
      is_ship_tomorrow: isShipTomorrow,
      num_of_drops: 1,
      is_roundtrip: isRoundTrip,
      contains_alcohol: false,
      distance: distance.toString(),
      quote: price.toString(),
      quoted_time: route.duration / 60 / 60,
      item_count: itemNum,
      num_of_gophrs: numPeople,
      created_by: merchantAuth0ID,
      is_fork_lift: isForkLift,
      is_dolly: isDolly,
      is_hazmat: isHazmat,
      is_pallet_jack: isPalletJack,
      is_twic_card: isTWICCard,
      is_liftgate: isLiftgate,
      is_hand_loading: isHandLoadUnload,
      is_weather_protection: isWeatherProtection,
      is_medical: isMedical,
      customer_blob: {
        first_name: storeFirstName,
        last_name: storeLastName,
        store_name: storeName,
        phone: storePhone.trim(),
        address: storeAddress,
        address_1: storeAddress1,
        address_2: null,
        city: storeCity,
        state: storeState,
        zip: storeZip,
        country: storeCountry,
        lat: parseFloat(storeLat),
        lng: parseFloat(storeLng)
      },
      address_1: storeAddress1,
      address_2: null,
      city: storeCity,
      zip: storeZip,
      state: storeState,
      country: storeCountry,
      lat: parseFloat(storeLat),
      lng: parseFloat(storeLng),
      pick_up_instructions: pickupInstruction,
      drop_off_instructions: dropoffInstruction,
      description: JSON.stringify(defaultItems),
      merchant_order_id: merchantOrderID === '' || merchantOrderID == null ? null : merchantOrderID,
      billed_route: route,
      actions: {},
      orders: ordersData,
      is_invoice: isInvoice,
      invoice_to: billingInfo,
      is_onBehalf: true,
      placed_by: SUB,
      onbehalf_of: merchantAuth0ID,
      ...deferredBillingPayload,
      has_tql_rate_value: hasTqlRateValue,
      tql_rate_blob: hasTqlRateValue ? tqlRateBlob : {},
      is_over_size: false,
      drop_off_state: dropoffInfo.state || '',
      is_market_base_quote: false,
      submitted_quote_payload: buildSubmittedQuotePayload()
    };

    if (editingSavedShipmentId) {
      data.save_again = shipmentStatus === 'shipment_saved';
    }

    Promise.resolve(refreshRegularQuoteBeforeSubmit(data))
      .then(refreshedData => postShipmentWithFallback({
        url,
        method,
        data: refreshedData,
      }))
      .then(response => {
        if (shipmentStatus === 'shipment_ready_for_driver' && orderStatus === 'awaiting_driver') {
          // notifySuccess('tc', 'Shipment Created Successfully');
        } else {
          // notifySuccess('tc', 'Shipment Saved Successfully');
        }
        window.location.reload();
      })
      .catch(error => {
        const submissionError = buildSubmissionError(error);
        handleErrorSubmit(submissionError);
        setErrorMessageSubmit(submissionError);
        notify('tc', null, submissionError.errorMessage);
      });
  };

  const handleConfirmRouted = (shipmentStatus, orderStatus, isScheduled) => {
    let url;
    let method = 'post';
    let scheduledForData = null;
    const deferredBillingPayload = getDeferredBillingPayload({ shipmentStatus });

    if (deferredBillingPayload.errorMessage) {
      blockPaymentDependentSubmission(deferredBillingPayload.errorMessage);
      return;
    }

    if (dropoffInstruction === null || dropoffInstruction === '') {
      setDropoffInstruction('N/A');
    }
    if (scheduledFor) {
      scheduledForData = scheduledFor.toISOString();
    }
    if (editingActiveShipmentId) {
      url = shipment_urls.updateActiveRoutedShipment(editingActiveShipmentId);
      method = 'put';
    } else if (isScheduled) {
      url = shipment_urls.createRoutedScheduledShipment;
    } else {
      if (shipmentStatus === 'shipment_saved') {
        url = shipment_urls.createRoutedSavedShipment;
      } else {
        url = shipment_urls.createRoutedShipment;
      }
    }
    let pickOrder = {
      store_id: storeId,
      // Pickup location
      location_blob: {
        name: pickupInfo.name,
        first_name: pickupInfo.first_name,
        last_name: pickupInfo.last_name,
        phone: pickupInfo.phone,
        address: pickupInfo.address,
        address_1: pickupInfo.address_1,
        address_2: pickupInfo.address_2,
        city: pickupInfo.city,
        zip: pickupInfo.zip,
        state: pickupInfo.state,
        country: 'US',
        business_name: pickupInfo.business_name ? pickupInfo.business_name : '',
        lat: pickupInfo.lat,
        lng: pickupInfo.lng
      },
      weight: weight,
      description: JSON.stringify(pickupItems),
      pick_up_instructions: pickupInstruction,
      actions: {
        pick_up: pickupItems
      }
    };
    let dropOrder = {
      store_id: storeId,
      // Dropoff location
      location_blob: {
        name: dropoffInfo.name,
        first_name: dropoffInfo.first_name,
        last_name: dropoffInfo.last_name,
        phone: dropoffInfo.phone,
        address: dropoffInfo.address,
        address_1: dropoffInfo.address_1,
        address_2: dropoffInfo.address_2,
        city: dropoffInfo.city,
        zip: dropoffInfo.zip,
        state: dropoffInfo.state,
        country: 'US',
        business_name: dropoffInfo.business_name ? dropoffInfo.business_name : '',
        lat: dropoffInfo.lat,
        lng: dropoffInfo.lng
      },
      weight: weight,

      description: JSON.stringify(dropoffItems),
      drop_off_instructions: dropoffInstruction,
      actions: {
        drop_off: dropoffItems
      }
    };
    let waypointPickOrder = {
      store_id: storeId,
      // Dropoff location
      location_blob: {
        name: waypointInfo.name,
        first_name: waypointInfo.first_name,
        last_name: waypointInfo.last_name,
        phone: waypointInfo.phone,
        address: waypointInfo.address,
        address_1: waypointInfo.address_1,
        address_2: waypointInfo.address_2,
        city: waypointInfo.city,
        zip: waypointInfo.zip,
        state: waypointInfo.state,
        country: 'US',
        business_name: waypointInfo.business_name ? waypointInfo.business_name : '',
        lat: waypointInfo.lat,
        lng: waypointInfo.lng
      },
      weight: weight,
      description: JSON.stringify(waypointPickupItems),
      pick_up_instructions: waypointPickupInstruction,
      actions: {
        pick_up: waypointPickupItems
      }
    };
    let waypointDropOrder = {
      store_id: storeId,
      // Dropoff location
      location_blob: {
        name: waypointInfo.name,
        first_name: waypointInfo.first_name,
        last_name: waypointInfo.last_name,
        phone: waypointInfo.phone,
        address: waypointInfo.address,
        address_1: waypointInfo.address_1,
        address_2: waypointInfo.address_2,
        city: waypointInfo.city,
        zip: waypointInfo.zip,
        state: waypointInfo.state,
        country: 'US',
        business_name: waypointInfo.business_name ? waypointInfo.business_name : '',
        lat: waypointInfo.lat,
        lng: waypointInfo.lng
      },
      weight: weight,
      description: JSON.stringify(waypointDropoffItems),
      drop_off_instructions: waypointDropoffInstruction,
      actions: {
        drop_off: waypointDropoffItems
      }
    };

    let waypointPickOrdersArray = [pickOrder, waypointPickOrder, dropOrder];
    let waypointDropOrdersArray = [pickOrder, waypointDropOrder, dropOrder];
    let waypointPickAndDropOrdersArray = [pickOrder, waypointDropOrder, waypointPickOrder, dropOrder];
    let ordersData;

    if (isWaypointPickup && isWaypointDropoff) {
      ordersData = waypointPickAndDropOrdersArray;
    } else if (isWaypointPickup && !isWaypointDropoff) {
      ordersData = waypointPickOrdersArray;
    } else if (!isWaypointPickup && isWaypointDropoff) {
      ordersData = waypointDropOrdersArray;
    } else {
      ordersData = [pickOrder, dropOrder];
    }

    if (editingActiveShipmentId) {
      // Attach existing Order ids so the backend updates matched stops and creates/removes the rest.
      if (editOrderIds.pickupOrderId) pickOrder.order_id = editOrderIds.pickupOrderId;
      if (editOrderIds.dropoffOrderId) dropOrder.order_id = editOrderIds.dropoffOrderId;
      if (editOrderIds.waypointPickupOrderId) waypointPickOrder.order_id = editOrderIds.waypointPickupOrderId;
      if (editOrderIds.waypointDropoffOrderId) waypointDropOrder.order_id = editOrderIds.waypointDropoffOrderId;
    }

    let itemNum = 0;
    pickupItems.forEach(it => {
      itemNum += it.quantity;
    });

    let data = {
      status: shipmentStatus,
      market_id: storeMarketId,
      scheduled_for: scheduledForData,
      weight: weight,
      vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
      is_expedited: isExpedited.toString(),
      is_super_expedited: isSuperExpedited,
      is_ship_tomorrow: isShipTomorrow,
      num_of_drops: isWaypointDropoff ? 2 : 1,
      is_roundtrip: isRoundTrip,
      contains_alcohol: false,
      distance: distance.toString(),
      quote: price.toString(),
      quoted_time: route.duration / 60 / 60,
      item_count: itemNum,
      num_of_gophrs: numPeople,
      created_by: merchantAuth0ID,
      is_fork_lift: isForkLift,
      is_dolly: isDolly,
      is_hazmat: isHazmat,
      is_pallet_jack: isPalletJack,
      is_twic_card: isTWICCard,
      is_liftgate: isLiftgate,
      is_hand_loading: isHandLoadUnload,
      is_weather_protection: isWeatherProtection,
      is_medical: isMedical,
      customer_blob: {
        first_name: storeFirstName,
        last_name: storeLastName,
        store_name: storeName,
        phone: storePhone.trim(),
        address: storeAddress,
        address_1: storeAddress1,
        address_2: null,
        city: storeCity,
        state: storeState,
        zip: storeZip,
        country: storeCountry,
        lat: parseFloat(storeLat),
        lng: parseFloat(storeLng)
      },
      address_1: storeAddress1,
      address_2: null,
      city: storeCity,
      zip: storeZip,
      state: storeState,
      country: storeCountry,
      lat: parseFloat(storeLat),
      lng: parseFloat(storeLng),
      pick_up_instructions: pickupInstruction,
      drop_off_instructions: dropoffInstruction,
      // description: description,
      description: 'A Routed Direct Shipment',
      merchant_order_id: merchantOrderID === '' || merchantOrderID == null ? null : merchantOrderID,
      billed_route: route,
      actions: {},
      orders: ordersData,
      is_invoice: isInvoice,
      invoice_to: billingInfo,
      is_onBehalf: true,
      placed_by: SUB,
      onbehalf_of: merchantAuth0ID,
      ...deferredBillingPayload,
      submitted_quote_payload: buildSubmittedQuotePayload(),
    };

    postShipmentWithFallback({
      url,
      method,
      data,
    })
      .then(response => {
        if (shipmentStatus === 'shipment_ready_for_driver' && orderStatus === 'awaiting_driver') {
          //notifySuccess('tc', 'Shipment Created Successfully');
        } else {
          // notifySuccess('tc', 'Shipment Saved Successfully');
        }
        window.location.reload();
      })
      .catch(error => {
        const submissionError = buildSubmissionError(error);
        handleErrorSubmit(submissionError);
        setErrorMessageSubmit(submissionError);
        notify('tc', null, submissionError.errorMessage);
      });
  };

  // eslint-disable-next-line no-unused-vars
  const handleConfirmMultiDrop = (shipmentStatus, orderStatus, isScheduled) => {
    let url;
    let method = 'post';
    let scheduledForData = null;
    const normalizedQuote = price !== null && price !== undefined && price !== '' ? String(price) : null;
    const deferredBillingPayload = getDeferredBillingPayload({ shipmentStatus });

    if (deferredBillingPayload.errorMessage) {
      blockPaymentDependentSubmission(deferredBillingPayload.errorMessage);
      return;
    }

    if (dropoffInstruction === null || dropoffInstruction === '') {
      setDropoffInstruction('N/A');
    }

    if (!normalizedQuote || !activeQuotePayload || !activeQuotePayload.quote_id) {
      const submissionError = {
        errorMessage: 'Refresh the shipping quote before submitting this multidrop order.',
        errorCode: 10,
      };
      handleErrorSubmit(submissionError);
      setErrorMessageSubmit(submissionError);
      notify('tc', null, submissionError.errorMessage);
      return;
    }

    if (scheduledFor) {
      scheduledForData = scheduledFor.toISOString();
    }
    if (editingActiveShipmentId) {
      url = shipment_urls.updateActiveMultidropShipment(editingActiveShipmentId);
      method = 'put';
    } else if (isScheduled) {
      url = shipment_urls.createMultidropScheduledShipmentWithRate;
    } else {
      if (shipmentStatus === 'shipment_saved') {
        url = shipment_urls.createMultidropSavedShipment;
      } else {
        url = shipment_urls.createMultidropShipmentWithRate;
      }
    }
    let pickOrder = {
      store_id: storeId,
      // Pickup location
      location_blob: {
        name: pickupInfo.name,
        first_name: pickupInfo.first_name,
        last_name: pickupInfo.last_name,
        phone: pickupInfo.phone,
        address: pickupInfo.address,
        address_1: pickupInfo.address_1,
        address_2: pickupInfo.address_2,
        city: pickupInfo.city,
        zip: pickupInfo.zip,
        state: pickupInfo.state,
        country: 'US',
        business_name: pickupInfo.business_name ? pickupInfo.business_name : '',
        lat: pickupInfo.lat,
        lng: pickupInfo.lng
      },
      weight: weight,
      description: JSON.stringify(pickupItems),
      pick_up_instructions: pickupInstruction,
      actions: {
        pick_up: pickupItems
      }
    };

    let multiDropOrders = multiDropsInfo.map((drop, index) => {
      return {
        store_id: storeId,
        location_blob: {
          ...drop
        },
        weight: weight,
        description: JSON.stringify(multiDropWaypointsArray[index].dropItems),
        drop_off_instructions: multiDropWaypointsArray[index].waypointDropoffInstruction,
        actions: {
          drop_off: multiDropWaypointsArray[index].dropItems
        }
      };
    });

    let ordersData = [pickOrder, ...multiDropOrders];

    let itemNum = 0;
    pickupItems.forEach(it => {
      itemNum += it.quantity;
    });

    let data = {
      status: shipmentStatus,
      market_id: storeMarketId,
      scheduled_for: scheduledForData,
      weight: weight,
      vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
      is_expedited: isExpedited.toString(),
      is_super_expedited: isSuperExpedited,
      is_ship_tomorrow: isShipTomorrow,
      num_of_drops: multiDropWaypointsArray.length,
      is_roundtrip: isRoundTrip,
      contains_alcohol: false,
      distance: distance.toString(),
      quote: normalizedQuote,
      quote_id: activeQuotePayload.quote_id,
      quoted_time: route.duration / 60 / 60,
      // is_itemized: perItemPrice && multiDropWaypointsArray.length > 1,
      // per_item_price: perItemPrice,
      item_count: itemNum,
      num_of_gophrs: numPeople,
      created_by: merchantAuth0ID,
      is_fork_lift: isForkLift,
      is_dolly: isDolly,
      is_hazmat: isHazmat,
      is_pallet_jack: isPalletJack,
      is_twic_card: isTWICCard,
      is_liftgate: isLiftgate,
      is_hand_loading: isHandLoadUnload,
      is_weather_protection: isWeatherProtection,
      is_medical: isMedical,
      customer_blob: {
        first_name: storeFirstName,
        last_name: storeLastName,
        store_name: storeName,
        phone: storePhone.trim(),
        address: storeAddress,
        address_1: storeAddress1,
        address_2: null,
        city: storeCity,
        state: storeState,
        zip: storeZip,
        country: storeCountry,
        lat: parseFloat(storeLat),
        lng: parseFloat(storeLng)
      },
      address_1: storeAddress1,
      address_2: null,
      city: storeCity,
      zip: storeZip,
      state: storeState,
      country: storeCountry,
      lat: parseFloat(storeLat),
      lng: parseFloat(storeLng),
      pick_up_instructions: pickupInstruction,
      drop_off_instructions: dropoffInstruction,
      // description: description,
      description: 'A Routed Direct Shipment',
      merchant_order_id: merchantOrderID === '' || merchantOrderID == null ? null : merchantOrderID,
      billed_route: route,
      actions: {},
      orders: ordersData,
      is_invoice: isInvoice,
      invoice_to: billingInfo,
      is_onBehalf: true,
      placed_by: SUB,
      onbehalf_of: merchantAuth0ID,
      ...deferredBillingPayload,
      submitted_quote_payload: buildSubmittedQuotePayload(),
    };

    postShipmentWithFallback({
      url,
      method,
      data,
    })
      .then(response => {
        if (shipmentStatus === 'shipment_ready_for_driver' && orderStatus === 'awaiting_driver') {
          //notifySuccess('tc', 'Shipment Created Successfully');
        } else {
          // notifySuccess('tc', 'Shipment Saved Successfully');
        }
        window.location.reload();
      })
      .catch(error => {
        const submissionError = buildSubmissionError(error);
        handleErrorSubmit(submissionError);
        setErrorMessageSubmit(submissionError);
        notify('tc', null, submissionError.errorMessage);
      });
  };

  const handleConfirmItemizedMultiDrop = (shipmentStatus, orderStatus, isScheduled) => {
    let url;
    let method = 'post';
    let scheduledForData = null;
    const deferredBillingPayload = getDeferredBillingPayload({ shipmentStatus });

    if (deferredBillingPayload.errorMessage) {
      blockPaymentDependentSubmission(deferredBillingPayload.errorMessage);
      return;
    }

    if (!activeQuotePayload || !activeQuotePayload.quote_id) {
      const submissionError = {
        errorMessage: 'Refresh the shipping quote before submitting this multidrop order.',
        errorCode: 10,
      };
      handleErrorSubmit(submissionError);
      setErrorMessageSubmit(submissionError);
      notify('tc', null, submissionError.errorMessage);
      return;
    }

    if (dropoffInstruction === null || dropoffInstruction === '') {
      setDropoffInstruction('N/A');
    }
    if (scheduledFor) {
      scheduledForData = scheduledFor.toISOString();
    }
    if (editingActiveShipmentId) {
      url = shipment_urls.updateActiveMultidropShipment(editingActiveShipmentId);
      method = 'put';
    } else if (isScheduled) {
      url = shipment_urls.createMultidropScheduledShipmentWithRate;
    } else {
      if (shipmentStatus === 'shipment_saved') {
        url = shipment_urls.createMultidropSavedShipment;
      } else {
        url = shipment_urls.createMultidropShipmentWithRate;
      }
    }
    let pickOrder = {
      store_id: storeId,
      // Pickup location
      location_blob: {
        name: pickupInfo.name,
        first_name: pickupInfo.first_name,
        last_name: pickupInfo.last_name,
        phone: pickupInfo.phone,
        address: pickupInfo.address,
        address_1: pickupInfo.address_1,
        address_2: pickupInfo.address_2,
        city: pickupInfo.city,
        zip: pickupInfo.zip,
        state: pickupInfo.state,
        country: 'US',
        business_name: pickupInfo.business_name ? pickupInfo.business_name : '',
        lat: pickupInfo.lat,
        lng: pickupInfo.lng
      },
      weight: 50,
      description: JSON.stringify(pickupItems),
      pick_up_instructions: pickupInstruction,
      actions: {
        pick_up: pickupItems
      }
    };

    let multiDropOrders = multiDropsInfo.map((drop, index) => {
      return {
        store_id: storeId,
        location_blob: {
          ...drop
        },
        weight: 50,
        description: JSON.stringify(multiDropWaypointsArray[index].dropItems),
        drop_off_instructions: multiDropWaypointsArray[index].waypointDropoffInstruction,
        actions: {
          drop_off: multiDropWaypointsArray[index].dropItems
        }
      };
    });

    let ordersData = [pickOrder, ...multiDropOrders];

    let itemNum = 0;
    pickupItems.forEach(it => {
      itemNum += it.quantity;
    });

    let itemsCountPerStop = [];
    multiDropWaypointsArray.forEach(ar => {
      let count = 0;
      ar.dropItems.forEach(it => {
        count += it.quantity;
      });
      itemsCountPerStop.push(count);
    });

    let data = {
      status: shipmentStatus,
      market_id: storeMarketId,
      scheduled_for: scheduledForData,
      weight: 50,
      vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
      is_expedited: isExpedited.toString(),
      is_super_expedited: isSuperExpedited,
      is_ship_tomorrow: isShipTomorrow,
      num_of_drops: multiDropWaypointsArray.length,
      is_roundtrip: isRoundTrip,
      contains_alcohol: false,
      distance: distance.toString(),
      quote: price.toString(),
      quote_id: activeQuotePayload.quote_id,
      quoted_time: route.duration / 60 / 60,
      is_itemized: true,
      per_item_price: perItemPrice,
      per_stop_price: perStopPrice,
      extra_price_per_item: extraPerItemPrice,
      free_items_count_per_stop: freeItemCount,
      items_count_per_stop_array: itemsCountPerStop,
      item_count: itemNum,
      num_of_gophrs: numPeople,
      created_by: merchantAuth0ID,
      is_fork_lift: isForkLift,
      is_dolly: isDolly,
      is_hazmat: isHazmat,
      is_pallet_jack: isPalletJack,
      is_twic_card: isTWICCard,
      is_liftgate: isLiftgate,
      is_hand_loading: isHandLoadUnload,
      is_weather_protection: isWeatherProtection,
      is_medical: isMedical,
      customer_blob: {
        first_name: storeFirstName,
        last_name: storeLastName,
        store_name: storeName,
        phone: storePhone.trim(),
        address: storeAddress,
        address_1: storeAddress1,
        address_2: null,
        city: storeCity,
        state: storeState,
        zip: storeZip,
        country: storeCountry,
        lat: parseFloat(storeLat),
        lng: parseFloat(storeLng)
      },
      address_1: storeAddress1,
      address_2: null,
      city: storeCity,
      zip: storeZip,
      state: storeState,
      country: storeCountry,
      lat: parseFloat(storeLat),
      lng: parseFloat(storeLng),
      pick_up_instructions: pickupInstruction,
      drop_off_instructions: dropoffInstruction,
      // description: description,
      description: 'A Routed Direct Shipment',
      merchant_order_id: merchantOrderID === '' || merchantOrderID == null ? null : merchantOrderID,
      billed_route: route,
      actions: {},
      orders: ordersData,
      is_invoice: isInvoice,
      invoice_to: billingInfo,
      is_onBehalf: true,
      placed_by: SUB,
      onbehalf_of: merchantAuth0ID,
      ...deferredBillingPayload,
      submitted_quote_payload: buildSubmittedQuotePayload(),
    };

    postShipmentWithFallback({
      url,
      method,
      data,
    })
      .then(response => {
        if (shipmentStatus === 'shipment_ready_for_driver' && orderStatus === 'awaiting_driver') {
          //notifySuccess('tc', 'Shipment Created Successfully');
        } else {
          // notifySuccess('tc', 'Shipment Saved Successfully');
        }
        window.location.reload();
      })
      .catch(error => {
        const submissionError = buildSubmissionError(error);
        handleErrorSubmit(submissionError);
        setErrorMessageSubmit(submissionError);
        notify('tc', null, submissionError.errorMessage);
      });
  };

  return (
    <div className="content">
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      {abandonedPrefillBanner ? (
        <Card style={{ backgroundColor: '#1f2a44', color: '#fff', border: '1px solid #1d8cf8', padding: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', lineHeight: 1.6 }}>
              <div style={{ color: '#1d8cf8', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', fontSize: '12px' }}>Abandoned cart — win-back order</div>
              <div><strong>Customer:</strong> {(abandonedPrefillBanner.customer && abandonedPrefillBanner.customer.name) || 'Unknown'}
                {abandonedPrefillBanner.customer && abandonedPrefillBanner.customer.phone ? ` · ${abandonedPrefillBanner.customer.phone}` : ''}
                {abandonedPrefillBanner.customer && abandonedPrefillBanner.customer.email ? ` · ${abandonedPrefillBanner.customer.email}` : ''}
              </div>
              <div><strong>Pickup:</strong> {(abandonedPrefillBanner.pickup && (abandonedPrefillBanner.pickup.address || [abandonedPrefillBanner.pickup.address_1, abandonedPrefillBanner.pickup.city, abandonedPrefillBanner.pickup.state, abandonedPrefillBanner.pickup.zip].filter(Boolean).join(', '))) || '—'}</div>
              <div><strong>Dropoff:</strong> {(abandonedPrefillBanner.dropoff && (abandonedPrefillBanner.dropoff.address || [abandonedPrefillBanner.dropoff.address_1, abandonedPrefillBanner.dropoff.city, abandonedPrefillBanner.dropoff.state, abandonedPrefillBanner.dropoff.zip].filter(Boolean).join(', '))) || '—'}</div>
              <div><strong>Quoted:</strong> {abandonedPrefillBanner.quoted_fee != null ? `$${Number(abandonedPrefillBanner.quoted_fee).toFixed(2)}` : '—'}
                {abandonedPrefillBanner.vehicle_type ? ` · ${String(abandonedPrefillBanner.vehicle_type).replace(/_/g, ' ')}` : ''}
                {abandonedPrefillBanner.scheduled_for ? ` · scheduled` : ''}
              </div>
              {abandonedPrefillBanner.store && abandonedPrefillBanner.store.store_id ? (
                <div><strong>On behalf of:</strong> {abandonedPrefillBanner.store.name} (auto-selected)</div>
              ) : null}
              <div style={{ color: '#aeb6c7', fontSize: '12px', marginTop: '6px' }}>
                {abandonedPrefillBanner.store && abandonedPrefillBanner.store.store_id
                  ? 'Merchant, vehicle, schedule, and accessorials are prefilled. Confirm the addresses above and add items.'
                  : 'Vehicle, schedule, and accessorials are prefilled. This customer has no merchant account — pick a billing store, confirm the addresses, and add items.'}
              </div>
            </div>
            <button type="button" className="btn btn-simple btn-sm" onClick={() => setAbandonedPrefillBanner(null)} style={{ margin: 0 }}>Dismiss</button>
          </div>
        </Card>
      ) : null}
      <Collapse id="collapse" isOpen={showFormSelection || Boolean(selectedStore && selectedType)}>
        <Card style={{ backgroundColor: '#27293d', color: '#fff' }} className="card-form">
          <form className="dropship-container" autoComplete="off" style={{ padding: '15px' }}>
            <CardTitle id="form-top" ref={formTopRef} tag="h2" style={{ padding: 20 }}>
              Create a Delivery
            </CardTitle>
            <div hidden={true}>
              <p style={{ color: 'red', marginTop: '4px', paddingBottom: 20 }}>Please fix the problems on this form</p>
            </div>
            <div style={{ display: 'flex', gap: '50px', marginBottom: '30px' }}>
              <MerchantSelections selectedStore={selectedStore} setSelectedStore={setSelectedStore} />
              <DirectTypeSelections selectedType={selectedType} setSelectedType={setSelectedType} />
            </div>
            {(storeId === 'ca3da560-d95f-46e4-a091-e588f67e3e2a' || storeId === 'a0eab2c4-7f10-4f17-9041-1149c7dcb6ce') && !showForm3 && selectedType !== 'multidrop' && (
              <GophrBillingForm
                isInvoice={isInvoice}
                setIsInvoice={setIsInvoice}
                billingInfo={billingInfo}
                setBillingInfo={setBillingInfo}
                billingErrors={billingErrors}
                vehicleType={vehicleType}
                isInvoiceDisabled={isInvoiceDisabled}
              />
            )}
            {selectedStore && !showForm3 && selectedType !== 'multidrop' && (
              <div style={{ marginLeft: '20px', marginRight: '20px', marginBottom: '25px', padding: '18px', border: '1px solid #344675', borderRadius: '12px', backgroundColor: '#1f2133' }}>
                <CardTitle tag="h4" style={{ padding: 0, marginBottom: '10px' }}>
                  Payment Terms
                </CardTitle>
                <div style={{ color: '#fff', fontSize: '14px', marginBottom: '8px' }}>
                  {getCompanyPaymentTermLabel(paymentProfileState.invoiceFrequency || 'single')}
                </div>
                <div style={{ color: '#cdcaca', fontSize: '12px', marginBottom: '12px' }}>
                  {getCompanyPaymentTermPolicy({
                    paymentTerm: paymentProfileState.invoiceFrequency || 'single',
                    isQuoteOnly: (((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) || vehicleType === 'car_hauler'),
                    isAdminOnBehalf: true,
                  }).description}
                </div>
                {paymentProfileState.fetchFailed ? (
                  <div style={{ color: '#ff8f8f', fontSize: '12px', marginBottom: '12px' }}>
                    The store payment profile could not be loaded. Refresh the page before submitting this order.
                  </div>
                ) : null}
                {!paymentProfileState.fetchFailed && paymentProfileState.loaded && getCompanyPaymentTermPolicy({
                  paymentTerm: paymentProfileState.invoiceFrequency || 'single',
                  isQuoteOnly: (((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) || vehicleType === 'car_hauler'),
                  isAdminOnBehalf: true,
                }).requiresSavedCardOnFile && !(selectedPaymentMethodId || paymentProfileState.selectedPaymentMethodId || paymentProfileState.defaultPaymentMethodId) ? (
                  <div style={{ color: '#f7c56b', fontSize: '12px', marginBottom: '12px' }}>
                    This store requires a saved card before a scheduled or ready order can be submitted.
                  </div>
                ) : null}
                <PaymentMethodDisplay
                  shouldFetch={Boolean(storeId)}
                  provisionMerchantProfile={false}
                  currentUserProfileFallback={false}
                  includeCustomerCards={true}
                  adminStoreProfileLookup={true}
                  storeId={storeId}
                  merchantAuth0Ids={merchantAuth0ID ? [merchantAuth0ID] : []}
                  merchantProfileSeed={{
                    auth0Id: merchantAuth0ID || '',
                    auth0Ids: merchantAuth0ID ? [merchantAuth0ID] : [],
                    first_name: (selectedStoreBillingOwner && selectedStoreBillingOwner.firstname) || storeFirstName || '',
                    last_name: (selectedStoreBillingOwner && selectedStoreBillingOwner.lastname) || storeLastName || '',
                    email: (selectedStoreBillingOwner && selectedStoreBillingOwner.email) || '',
                    billing_email: (selectedStoreBillingOwner && selectedStoreBillingOwner.email) || '',
                    store_id: storeId || '',
                    store_name: storeName || '',
                  }}
                  cardAuth0Id={merchantAuth0ID || null}
                  onPaymentMethodSelect={setSelectedPaymentMethodId}
                  selectedPaymentMode={selectedPaymentMode}
                  onPaymentModeChange={setSelectedPaymentMode}
                  onPaymentProfileStateChange={setPaymentProfileState}
                  paymentProfileState={paymentProfileState}
                />
              </div>
            )}
            {selectedStore && selectedType === 'regular' && (
              <RegularCreation
                showForm1={showForm1}
                setShowForm1={setShowForm1}
                showForm2={showForm2}
                setShowForm2={setShowForm2}
                setShowForm3={setShowForm3}
                pickupInfo={pickupInfo}
                setPickupInfo={setPickupInfo}
                pickupInstruction={pickupInstruction}
                setPickupInstruction={setPickupInstruction}
                merchantOrderID={merchantOrderID}
                setMerchantOrderID={setMerchantOrderID}
                vehicleType={vehicleType}
                setVehicleType={setVehicleType}
                numPeople={numPeople}
                setNumPeople={setNumPeople}
                scheduledFor={scheduledFor}
                setScheduledFor={setScheduledFor}
                setIsExpedited={setIsExpedited}
                isScheduled={isScheduled}
                setIsScheduled={setIsScheduled}
                price={price}
                setPrice={setPrice}
                isSaved={isSaved}
                setIsSaved={setIsSaved}
                setOrderStatus={setOrderStatus}
                setShipmentStatus={setShipmentStatus}
                dropoffInstruction={dropoffInstruction}
                setDropoffInstruction={setDropoffInstruction}
                storeFirstName={storeFirstName}
                storeLastName={storeLastName}
                storePhone={storePhone}
                storeAddress={storeAddress}
                storeAddress1={storeAddress1}
                storeAddress2={storeAddress2}
                storeCity={storeCity}
                storeZip={storeZip}
                storeState={storeState}
                storeCountry={storeCountry}
                storeName={storeName}
                storeLat={storeLat}
                storeLng={storeLng}
                weight={weight}
                setWeight={setWeight}
                defaultItems={defaultItems}
                setDefaultItems={setDefaultItems}
                dropoffInfo={dropoffInfo}
                setDropoffInfo={setDropoffInfo}
                storeMarketId={storeMarketId}
                setIsSuperExpedited={setIsSuperExpedited}
                isRoundTrip={isRoundTrip}
                setDistance={setDistance}
                setRoute={setRoute}
                errorSubmit={errorSubmit}
                handleErrorSubmit={handleErrorSubmit}
                notify={notify}
                setErrorMessageDistance={setErrorMessageDistance}
                errorMessageSubmit={errorMessageSubmit}
                setErrorMessageSubmit={setErrorMessageSubmit}
                storeId={storeId}
                vendorLat={vendorLat}
                vendorLong={vendorLong}
                isExpedited={isExpedited}
                isSuperExpedited={isSuperExpedited}
                setIsForkLift={setIsForkLift}
                isForkLift={isForkLift}
                setIsDolly={setIsDolly}
                isDolly={isDolly}
                setIsHandLoadUnload={setIsHandLoadUnload}
                isHandLoadUnload={isHandLoadUnload}
                setIsHazmat={setIsHazmat}
                isHazmat={isHazmat}
                setIsHotBag={setIsHotBag}
                isHotBag={isHotBag}
                setIsCooler={setIsCooler}
                isCooler={isCooler}
                isTWICCard={isTWICCard}
                setIsTWICCard={setIsTWICCard}
                isLiftgate={isLiftgate}
                setIsLiftgate={setIsLiftgate}
                isPalletJack={isPalletJack}
                setIsPalletJack={setIsPalletJack}
                isWeatherProtection={isWeatherProtection}
                setIsWeatherProtection={setIsWeatherProtection}
                isMedical={isMedical}
                setIsMedical={setIsMedical}
                standardPriceRangeLow={standardPriceRangeLow}
                setStandardPriceRangeLow={setStandardPriceRangeLow}
                standardPriceRangeHigh={standardPriceRangeHigh}
                setStandardPriceRangeHigh={setStandardPriceRangeHigh}
                expeditedPriceRangeLow={expeditedPriceRangeLow}
                setExpeditedPriceRangeLow={setExpeditedPriceRangeLow}
                expeditedPriceRangeHigh={expeditedPriceRangeHigh}
                setExpeditedPriceRangeHigh={setExpeditedPriceRangeHigh}
                superExpeditedPriceRangeLow={superExpeditedPriceRangeLow}
                setSuperExpeditedPriceRangeLow={setSuperExpeditedPriceRangeLow}
                superExpeditedPriceRangeHigh={superExpeditedPriceRangeHigh}
                setSuperExpeditedPriceRangeHigh={setSuperExpeditedPriceRangeHigh}
                priceRangeLow={priceRangeLow}
                setPriceRangeLow={setPriceRangeLow}
                setPriceRangeHigh={setPriceRangeHigh}
                setStandardQuotePayload={setStandardQuotePayload}
                setExpeditedQuotePayload={setExpeditedQuotePayload}
                setSuperExpeditedQuotePayload={setSuperExpeditedQuotePayload}
                isInvoice={isInvoice}
                checkInvoice={checkInvoice}
                setHasTqlRateValue={setHasTqlRateValue}
                setTqlRateBlob={setTqlRateBlob}
                isShipTomorrow={isShipTomorrow}
                setIsShipTomorrow={setIsShipTomorrow}
              />
            )}
            {selectedStore && selectedType === 'routed' && (
              <RoutedCreation
                showForm1={showForm1}
                setShowForm1={setShowForm1}
                showForm2={showForm2}
                setShowForm2={setShowForm2}
                setShowForm3={setShowForm3}
                pickupInfo={pickupInfo}
                setPickupInfo={setPickupInfo}
                pickupInstruction={pickupInstruction}
                setPickupInstruction={setPickupInstruction}
                merchantOrderID={merchantOrderID}
                setMerchantOrderID={setMerchantOrderID}
                vehicleType={vehicleType}
                setVehicleType={setVehicleType}
                numPeople={numPeople}
                setNumPeople={setNumPeople}
                scheduledFor={scheduledFor}
                setScheduledFor={setScheduledFor}
                setIsExpedited={setIsExpedited}
                isScheduled={isScheduled}
                setIsScheduled={setIsScheduled}
                price={price}
                setPrice={setPrice}
                isSaved={isSaved}
                setIsSaved={setIsSaved}
                setOrderStatus={setOrderStatus}
                setShipmentStatus={setShipmentStatus}
                dropoffInstruction={dropoffInstruction}
                setDropoffInstruction={setDropoffInstruction}
                storeFirstName={storeFirstName}
                storeLastName={storeLastName}
                storePhone={storePhone}
                storeAddress={storeAddress}
                storeAddress1={storeAddress1}
                storeAddress2={storeAddress2}
                storeCity={storeCity}
                storeZip={storeZip}
                storeState={storeState}
                storeCountry={storeCountry}
                storeName={storeName}
                storeLat={storeLat}
                storeLng={storeLng}
                weight={weight}
                setWeight={setWeight}
                dropoffInfo={dropoffInfo}
                setDropoffInfo={setDropoffInfo}
                storeMarketId={storeMarketId}
                setIsSuperExpedited={setIsSuperExpedited}
                isRoundTrip={isRoundTrip}
                setIsRoundTrip={setIsRoundTrip}
                setDistance={setDistance}
                setRoute={setRoute}
                errorSubmit={errorSubmit}
                handleErrorSubmit={handleErrorSubmit}
                notify={notify}
                errorMessageDistance={errorMessageDistance}
                setErrorMessageDistance={setErrorMessageDistance}
                errorMessageSubmit={errorMessageSubmit}
                setErrorMessageSubmit={setErrorMessageSubmit}
                storeId={storeId}
                vendorLat={vendorLat}
                vendorLong={vendorLong}
                pickupItems={pickupItems}
                setPickupItems={setPickupItems}
                isExpedited={isExpedited}
                isSuperExpedited={isSuperExpedited}
                waypointInfo={waypointInfo}
                setWaypointInfo={setWaypointInfo}
                waypointPickupInstruction={waypointPickupInstruction}
                setWaypointPickupInstruction={setWaypointPickupInstruction}
                waypointDropoffInstruction={waypointDropoffInstruction}
                setWaypointDropoffInstruction={setWaypointDropoffInstruction}
                isWaypointPickup={isWaypointPickup}
                isWaypointDropoff={isWaypointDropoff}
                waypointPickupItems={waypointPickupItems}
                setWaypointPickupItems={setWaypointPickupItems}
                waypointDropoffItems={waypointDropoffItems}
                setWaypointDropoffItems={setWaypointDropoffItems}
                dropoffItems={dropoffItems}
                setDropoffItems={setDropoffItems}
                setIsWaypointPickup={setIsWaypointPickup}
                setIsWaypointDropoff={setIsWaypointDropoff}
                setIsForkLift={setIsForkLift}
                isForkLift={isForkLift}
                setIsDolly={setIsDolly}
                isDolly={isDolly}
                setIsHandLoadUnload={setIsHandLoadUnload}
                isHandLoadUnload={isHandLoadUnload}
                setIsHazmat={setIsHazmat}
                isHazmat={isHazmat}
                setIsHotBag={setIsHotBag}
                isHotBag={isHotBag}
                setIsCooler={setIsCooler}
                isCooler={isCooler}
                isTWICCard={isTWICCard}
                setIsTWICCard={setIsTWICCard}
                isLiftgate={isLiftgate}
                setIsLiftgate={setIsLiftgate}
                isPalletJack={isPalletJack}
                setIsPalletJack={setIsPalletJack}
                isWeatherProtection={isWeatherProtection}
                setIsWeatherProtection={setIsWeatherProtection}
                isMedical={isMedical}
                setIsMedical={setIsMedical}
                standardPriceRangeLow={standardPriceRangeLow}
                setStandardPriceRangeLow={setStandardPriceRangeLow}
                standardPriceRangeHigh={standardPriceRangeHigh}
                setStandardPriceRangeHigh={setStandardPriceRangeHigh}
                expeditedPriceRangeLow={expeditedPriceRangeLow}
                setExpeditedPriceRangeLow={setExpeditedPriceRangeLow}
                expeditedPriceRangeHigh={expeditedPriceRangeHigh}
                setExpeditedPriceRangeHigh={setExpeditedPriceRangeHigh}
                superExpeditedPriceRangeLow={superExpeditedPriceRangeLow}
                setSuperExpeditedPriceRangeLow={setSuperExpeditedPriceRangeLow}
                superExpeditedPriceRangeHigh={superExpeditedPriceRangeHigh}
                setSuperExpeditedPriceRangeHigh={setSuperExpeditedPriceRangeHigh}
                priceRangeLow={priceRangeLow}
                setPriceRangeLow={setPriceRangeLow}
                setPriceRangeHigh={setPriceRangeHigh}
                setStandardQuotePayload={setStandardQuotePayload}
                setExpeditedQuotePayload={setExpeditedQuotePayload}
                setSuperExpeditedQuotePayload={setSuperExpeditedQuotePayload}
                isInvoice={isInvoice}
                checkInvoice={checkInvoice}
                setHasTqlRateValue={setHasTqlRateValue}
                setTqlRateBlob={setTqlRateBlob}
              />
            )}
            {selectedStore && selectedType === 'multidrop' && (
              <DropshipForm
                errorDistance={errorMessageDistance}
                errorSubmit={errorMessageSubmit}
                handleErrorDistance={setErrorMessageDistance}
                handleErrorSubmit={handleErrorSubmit}
                showForm={showForm1}
                showForm2={showForm2}
                handleForm={(nextShowForm, nextShowForm2) => {
                  setShowForm1(nextShowForm);
                  setShowForm2(nextShowForm2);
                }}
                notify={notify}
                notifySuccess={() => {}}
                prefilledStore={selectedStore}
                forceMultidrop={true}
                isAdminOnBehalf={true}
                onBehalfMerchantAuth0Id={merchantAuth0ID}
                abandonedPrefill={abandonedPrefillBanner}
                savedDraftEdit={editingActiveShipmentId && activeEditShipment ? { shipment: activeEditShipment, isActiveEdit: true } : null}
                onSavedDraftEditClose={() => history.push('/admin/dashboard')}
              />
            )}
            {selectedStore && selectedType === 'itemized-multidrop' && (
              <ItemizedMultiDropCreation
                showForm1={showForm1}
                setShowForm1={setShowForm1}
                showForm2={showForm2}
                setShowForm2={setShowForm2}
                setShowForm3={setShowForm3}
                pickupInfo={pickupInfo}
                setPickupInfo={setPickupInfo}
                pickupInstruction={pickupInstruction}
                setPickupInstruction={setPickupInstruction}
                merchantOrderID={merchantOrderID}
                setMerchantOrderID={setMerchantOrderID}
                vehicleType={vehicleType}
                setVehicleType={setVehicleType}
                numPeople={numPeople}
                setNumPeople={setNumPeople}
                scheduledFor={scheduledFor}
                setScheduledFor={setScheduledFor}
                setIsExpedited={setIsExpedited}
                isScheduled={isScheduled}
                setIsScheduled={setIsScheduled}
                price={price}
                setPrice={setPrice}
                isSaved={isSaved}
                setIsSaved={setIsSaved}
                setOrderStatus={setOrderStatus}
                setShipmentStatus={setShipmentStatus}
                storeFirstName={storeFirstName}
                storeLastName={storeLastName}
                storePhone={storePhone}
                storeAddress={storeAddress}
                storeAddress1={storeAddress1}
                storeAddress2={storeAddress2}
                storeCity={storeCity}
                storeZip={storeZip}
                storeState={storeState}
                storeCountry={storeCountry}
                storeName={storeName}
                storeLat={storeLat}
                storeLng={storeLng}
                weight={weight}
                setWeight={setWeight}
                storeMarketId={storeMarketId}
                setIsSuperExpedited={setIsSuperExpedited}
                isRoundTrip={isRoundTrip}
                setDistance={setDistance}
                setRoute={setRoute}
                errorSubmit={errorSubmit}
                handleErrorSubmit={handleErrorSubmit}
                notify={notify}
                errorMessageDistance={errorMessageDistance}
                setErrorMessageDistance={setErrorMessageDistance}
                errorMessageSubmit={errorMessageSubmit}
                setErrorMessageSubmit={setErrorMessageSubmit}
                storeId={storeId}
                vendorLat={vendorLat}
                vendorLong={vendorLong}
                pickupItems={pickupItems}
                setPickupItems={setPickupItems}
                multiDropWaypointsArray={multiDropWaypointsArray}
                setMultiDropWaypointsArray={setMultiDropWaypointsArray}
                multiDropWaypointsErrors={multiDropWaypointsErrors}
                setMultiDropWaypointsErrors={setMultiDropWaypointsErrors}
                setMultiDropsInfo={setMultiDropsInfo}
                isExpedited={isExpedited}
                isSuperExpedited={isSuperExpedited}
                perItemPrice={perItemPrice}
                setPerItemPrice={setPerItemPrice}
                perStopPrice={perStopPrice}
                setPerStopPrice={setPerStopPrice}
                extraPerItemPrice={extraPerItemPrice}
                setExtraPerItemPrice={setExtraPerItemPrice}
                freeItemCount={freeItemCount}
                setFreeItemCount={setFreeItemCount}
                setIsForkLift={setIsForkLift}
                isForkLift={isForkLift}
                setIsDolly={setIsDolly}
                isDolly={isDolly}
                setIsHandLoadUnload={setIsHandLoadUnload}
                isHandLoadUnload={isHandLoadUnload}
                setIsHazmat={setIsHazmat}
                isHazmat={isHazmat}
                setIsHotBag={setIsHotBag}
                isHotBag={isHotBag}
                setIsCooler={setIsCooler}
                isCooler={isCooler}
                isTWICCard={isTWICCard}
                setIsTWICCard={setIsTWICCard}
                isLiftgate={isLiftgate}
                setIsLiftgate={setIsLiftgate}
                isPalletJack={isPalletJack}
                setIsPalletJack={setIsPalletJack}
                isWeatherProtection={isWeatherProtection}
                setIsWeatherProtection={setIsWeatherProtection}
                isMedical={isMedical}
                setIsMedical={setIsMedical}
                standardPriceRangeLow={standardPriceRangeLow}
                setStandardPriceRangeLow={setStandardPriceRangeLow}
                standardPriceRangeHigh={standardPriceRangeHigh}
                setStandardPriceRangeHigh={setStandardPriceRangeHigh}
                expeditedPriceRangeLow={expeditedPriceRangeLow}
                setExpeditedPriceRangeLow={setExpeditedPriceRangeLow}
                expeditedPriceRangeHigh={expeditedPriceRangeHigh}
                setExpeditedPriceRangeHigh={setExpeditedPriceRangeHigh}
                superExpeditedPriceRangeLow={superExpeditedPriceRangeLow}
                setSuperExpeditedPriceRangeLow={setSuperExpeditedPriceRangeLow}
                superExpeditedPriceRangeHigh={superExpeditedPriceRangeHigh}
                setSuperExpeditedPriceRangeHigh={setSuperExpeditedPriceRangeHigh}
                priceRangeLow={priceRangeLow}
                setPriceRangeLow={setPriceRangeLow}
                setPriceRangeHigh={setPriceRangeHigh}
                setStandardQuotePayload={setStandardQuotePayload}
                setExpeditedQuotePayload={setExpeditedQuotePayload}
                setSuperExpeditedQuotePayload={setSuperExpeditedQuotePayload}
                isInvoice={isInvoice}
                checkInvoice={checkInvoice}
              />
            )}
          </form>
        </Card>
      </Collapse>
      {showForm3 && selectedStore && selectedType !== 'multidrop' && (
        <div style={{ backgroundColor: '#27293d', paddingTop: 10 }}>
          <PreferredDriverSelect
            pickupLat={(pickupInfo && pickupInfo.lat) || storeLat}
            pickupLng={(pickupInfo && pickupInfo.lng) || storeLng}
            marketId={storeMarketId}
            vehicleType={vehicleType}
            value={preferredDriver}
            onChange={setPreferredDriver}
          />
        </div>
      )}
      {selectedType === 'regular' && (
        <RegularConfirmPage
          showForm3={showForm3}
          pickupInfo={pickupInfo}
          pickupInstruction={pickupInstruction}
          merchantOrderID={merchantOrderID}
          vehicleType={vehicleType}
          numPeople={numPeople}
          scheduledFor={scheduledFor}
          isExpedited={isExpedited}
          isScheduled={isScheduled}
          handleBack={handleBack}
          price={price}
          handleConfirmRegular={handleConfirmRegular}
          setIsSaved={setIsSaved}
          setOrderStatus={setOrderStatus}
          setShipmentStatus={setShipmentStatus}
          dropoffInfo={dropoffInfo}
          dropoffInstruction={dropoffInstruction}
          defaultItems={defaultItems}
          priceRangeLow={priceRangeLow}
          priceRangeHigh={priceRangeHigh}
          quoteLineItems={activeQuoteLineItems}
          isInvoice={isInvoice}
          billingInfo={billingInfo}
        />
      )}
      {selectedType === 'routed' && (
        <RoutedConfirmPage
          showForm3={showForm3}
          pickupInfo={pickupInfo}
          pickupInstruction={pickupInstruction}
          merchantOrderID={merchantOrderID}
          vehicleType={vehicleType}
          numPeople={numPeople}
          scheduledFor={scheduledFor}
          isExpedited={isExpedited}
          isScheduled={isScheduled}
          handleBack={handleBack}
          price={price}
          handleConfirmRouted={handleConfirmRouted}
          setIsSaved={setIsSaved}
          setOrderStatus={setOrderStatus}
          setShipmentStatus={setShipmentStatus}
          dropoffInfo={dropoffInfo}
          dropoffInstruction={dropoffInstruction}
          waypointInfo={waypointInfo}
          waypointPickupInstruction={waypointPickupInstruction}
          waypointDropoffInstruction={waypointDropoffInstruction}
          pickupItems={pickupItems}
          isWaypointPickup={isWaypointPickup}
          waypointPickupItems={waypointPickupItems}
          priceRangeLow={priceRangeLow}
          priceRangeHigh={priceRangeHigh}
          quoteLineItems={activeQuoteLineItems}
          isInvoice={isInvoice}
          billingInfo={billingInfo}
          editingActiveShipmentId={editingActiveShipmentId}
          originalPrice={originalPrice}
        />
      )}
      {selectedType === 'itemized-multidrop' && (
        <ItemizedMultiDropConfirmPage
          showForm3={showForm3}
          pickupInfo={pickupInfo}
          pickupInstruction={pickupInstruction}
          merchantOrderID={merchantOrderID}
          vehicleType={vehicleType}
          numPeople={numPeople}
          scheduledFor={scheduledFor}
          isExpedited={isExpedited}
          isScheduled={isScheduled}
          handleBack={handleBack}
          price={price}
          handleConfirmItemizedMultiDrop={handleConfirmItemizedMultiDrop}
          setIsSaved={setIsSaved}
          setOrderStatus={setOrderStatus}
          setShipmentStatus={setShipmentStatus}
          pickupItems={pickupItems}
          multiDropWaypointsArray={multiDropWaypointsArray}
          perItemPrice={perItemPrice}
          priceRangeLow={priceRangeLow}
          priceRangeHigh={priceRangeHigh}
          perStopPrice={perStopPrice}
          freeItemCount={freeItemCount}
          extraPerItemPrice={extraPerItemPrice}
          quoteLineItems={activeQuoteLineItems}
          isInvoice={isInvoice}
          billingInfo={billingInfo}
        />
      )}
    </div>
  );
};

export default CreateDeliveryForMerchant;
